import type { Messages } from './es';

export const en: Messages = {
  shell: {
    product: 'Accessibility, step by step',
    headline: 'A better website starts with knowing what to improve.',
    intro: 'Scan your websites, find barriers and organise your team’s work in one place.',
    connect: 'Connect your website',
    connectHint: 'Add your domain and choose its regulatory profile.',
    understand: 'Understand the barriers',
    understandHint: 'See problems, priorities and guidance to fix them.',
    improve: 'Make progress together',
    improveHint: 'Review the results and record your decisions.',
    note: 'Scores are indicative. Automated analysis should be complemented with a manual review to assess whether accessibility requirements are met.',
  },
  app: {
    name: 'SiteNorma',
    home: 'SiteNorma, dashboard home',
    website: 'SiteNorma, website home',
    skip: 'Skip to content',
    language: 'Language',
    userMenu: 'User menu',
    signOut: 'Sign out',
    loading: 'Loading…',
    retry: 'Try again',
    newTab: 'opens in a new tab',
  },
  password: {
    show: 'Show password',
    hide: 'Hide password',
    rules: {
      min: 'At least {{min}} characters',
      max: 'No more than {{max}} characters',
      met: 'met',
      unmet: 'not met',
    },
    errors: {
      min: 'The password needs at least {{min}} characters.',
      max: 'The password cannot be longer than {{max}} characters.',
    },
    nowVisible: 'The password is visible on screen.',
    nowHidden: 'The password is hidden.',
  },
  notFound: {
    title: 'Page not found',
    text: 'This address does not exist or has changed.',
    home: 'Go to the dashboard',
  },
  form: {
    requiredNote: 'Fields marked with * are required.',
  },
  errors: {
    title: 'Something went wrong',
    generic: 'We could not complete the request. Please try again.',
    required: 'Fill in this field.',
    tooShort: 'Write at least {{min}} characters.',
    invalidCredentials: 'The email or the password is not correct.',
    userExists: 'There is already an account with that email. Sign in or recover your password.',
  },
  forms: {
    email: {
      required: 'Enter your email address.',
      invalid: 'Enter a valid email address.',
    },
    password: {
      required: 'Enter your password.',
    },
    privacy: {
      required: 'You must tick the privacy box to create the account.',
    },
    verifyLink: 'This link is not valid or has expired. Request a new verification email.',
    limit: {
      before: 'Too many attempts. You will be able to try again in',
      seconds_one: '{{count}} second.',
      seconds_other: '{{count}} seconds.',
      over: 'You can try again now.',
      unknown: 'Too many attempts. Wait a moment and try again.',
    },
  },
  google: {
    or: 'or',
    login: 'Continue with Google',
    signup: 'Sign up with Google',
    connecting: 'Connecting to Google…',
    errors: {
      cancelled: 'You cancelled signing in with Google. You can try again or use your email and password.',
      noAccount: 'There is no SiteNorma account for that Google account. Create one first.',
      notLinked:
        'An account with the email of your Google account already exists, but it is not verified yet. Verify it with the link we sent you or sign in with your password.',
      notVerified: 'Google has not confirmed that email address, so we cannot use it for your account.',
      failed: 'We could not continue with Google. Try again or use your email and password.',
    },
  },
  login: {
    title: 'Sign in',
    subtitle: 'Access your account with your email and password.',
    email: 'Email address',
    password: 'Password',
    submit: 'Sign in',
    forgot: 'Forgotten your password?',
    noAccount: 'Don’t have an account?',
    signup: 'Create your account',
    submitting: 'Signing in…',
  },
  signup: {
    title: 'Create your SiteNorma account',
    benefit: 'Manage your accessibility analyses and see their results in one place.',
    privacy: {
      label:
        'I have read and accept the <privacy>privacy policy</privacy> and the <terms>terms of use</terms>. We will use your email address to manage your account and to send you communications related to the service.',
      language: 'These documents are in English.',
    },
    email: 'Email address',
    password: 'Password',
    verify: 'To activate your account, you will need to verify your email address.',
    submit: 'Create account',
    submitting: 'Creating account…',
    haveAccount: 'Already have an account?',
    login: 'Sign in',
  },
  verify: {
    title: 'Verify your email address',
    sent: 'We have sent a verification email to {{email}}. Open the link to activate your account. The link expires in one hour.',
    notSent:
      'Your account has been created, but we could not send the verification email to {{email}}. It is still waiting to be verified: try again, you do not need to sign up again.',
    pendingLogin:
      'To sign in, you need to verify your email address. We have sent a link to {{email}}; if you can no longer find it, you can ask for another.',
    spam: 'If you cannot find the message, check your spam folder and make sure the address is correct.',
    resend: 'Resend verification email',
    resending: 'Sending…',
    resent: 'We have sent another verification email to {{email}}.',
    wait: 'You will be able to ask for another email in',
    over: 'You can resend the verification email now.',
    correct: 'Correct email address',
    newEmail: 'Correct email address',
    correctHint:
      'We will send the link to the new address. Links sent to the old one will stop working and your account will stay pending until you verify the new one.',
    correctSubmit: 'Save and send verification',
    correctSubmitting: 'Saving…',
    cancel: 'Cancel',
    corrected:
      'We have sent a verification email to {{email}}. Links sent to the previous address no longer work.',
    sendFailed: 'We could not send the verification email. Your account is still pending: try again.',
    credentials: 'We could not check the account. Sign in again to continue.',
    back: 'Back to sign in',
    done: {
      okTitle: 'Email verified',
      ok: 'Your email address has been verified. You can now sign in to SiteNorma.',
      alreadyTitle: 'Account already verified',
      already: 'Your account is already verified.',
      invalidTitle: 'Link not valid',
      invalid: 'This link is not valid or has expired. Request a new verification email.',
      noneTitle: 'Verify your email address',
      none: 'To verify your email address, open the link in the message we sent you. If you do not have it, you can request a new one.',
      login: 'Sign in',
      request: 'Request a new email',
    },
    request: {
      title: 'Request a new verification email',
      intro: 'Enter the email address you created your account with.',
      email: 'Email address',
      submit: 'Request new email',
      submitting: 'Requesting…',
      sent: 'If there is an account waiting to be verified with this email address, you will receive a new link. Check your spam folder too.',
    },
  },
  forgot: {
    title: 'Recover access to your account',
    sentTitle: 'Check your email',
    intro: 'Enter the email address of your account to request a link to reset your password.',
    email: 'Email address',
    submit: 'Request link',
    sent: 'If an account is associated with this email address, you will receive a link to reset your password. Check your spam folder too.',
    back: 'Back to sign in',
    submitting: 'Requesting link…',
  },
  reset: {
    title: 'Set a new password',
    doneTitle: 'Password updated',
    password: 'New password',
    submit: 'Save password',
    done: 'Your password has been updated successfully.',
    invalid:
      'This link is not valid, has expired or has already been used. Request a new one to reset your password.',
    login: 'Sign in',
    submitting: 'Saving…',
    another: 'Request a new link',
    pending:
      'To sign in, you still need to verify your email address. When you sign in we will help you do it.',
  },
  organization: {
    title: 'Create your organisation',
    intro: 'Your organisation brings together your websites and the people you work with on them.',
    name: 'Organisation name',
    nameHint: 'For example, the name of your company or agency.',
    submit: 'Create organisation',
    nav: 'Organisation',
    navMembers: 'People',
    navBilling: 'Plan and billing',
  },
  billing: {
    title: 'Plan and billing',
    loadFailed: 'We could not load the plan. Try again.',
    myPlan: 'My plan',
    planName: '{{plan}} plan',
    includes: 'Includes',
    pagesLimit: 'Pages analysed per month',
    pagesPerMonth_one: '{{count}} page',
    pagesPerMonth_other: '{{count}} pages',
    pagesUnlimited: 'Unlimited',
    wpLimit: 'Websites linked with WordPress',
    wpSites_one: 'Up to {{count}} website',
    wpSites_other: 'Up to {{count}} websites',
    wpUnlimited: 'Unlimited',
    selectPages: 'Manual page selection',
    selectPagesYes: 'Included',
    selectPagesNo: 'Not included',
    seeOtherPlans: 'See other plans',
    usageOfMonth: 'Use in {{month}}',
    pagesAnalysed_one: '{{count}} page from this month’s quota',
    pagesAnalysed_other: '{{count}} pages from this month’s quota',
    pagesUsedOf: '{{used}} of {{limit}} pages from this month’s quota',
    pagesAvailable_one: '{{count}} page available',
    pagesAvailable_other: '{{count}} pages available',
    monthlyQuotaUnlimited: 'Monthly quota: unlimited.',
    usageLabel: 'Pages used from the monthly quota',
    usageHint:
      'Adds up the pages from all your scans this month: each one counts. On “Your websites”, “Pages scanned” only adds the latest scan of each website.',
    usageReserved_one: '{{count}} page reserved for scans queued or running.',
    usageReserved_other: '{{count}} pages reserved for scans queued or running.',
    quotaResets: 'The counter resets on {{date}}.',
    managementTitle: 'Management and billing',
    managementFree: 'Review other plans or contact our team to manage your subscription.',
    managementPaid: 'Review other plans or contact our team to manage your subscription.',
    readOnly:
      'You can view the plan and use. To change it or manage the subscription, ask an owner or administrator of the organisation.',
    seePlans: 'See plans',
    contactSupport: 'Contact support',
    assigned:
      'Our team manages your plan and a purchase does not change it. If you need to change it, write to us.',
    purchase: {
      title: 'Choose your plan',
      intro:
        'You pay for the pages we analyse each month, desktop and mobile included. Prices are without VAT.',
      period: 'Billing period',
      month: 'Monthly',
      year: 'Yearly',
      yearHint: '2 months free',
      perMonth: '{{price}} a month',
      perYear: '{{price}} a year',
      equivalent: 'Equivalent to {{price}} a month',
      buy: 'Get {{plan}}',
      redirecting: 'Opening the secure payment…',
      secure:
        'You pay on a secure Stripe page: SiteNorma never sees or stores your card details. You can cancel at any time and the plan stays active until the end of the period you paid for.',
      custom: 'Need more volume? Talk to us',
      errors: {
        failed: 'We could not open the payment. Try again.',
        'suscripcion-activa': 'You already have a subscription. To change plan, use “Manage subscription”.',
        'plan-asignado': 'Our team manages your plan and a purchase does not change it.',
        limite: 'You have tried too many times. Wait a little and try again.',
        permisos: 'Only the owners and admins of the organisation can get a plan.',
      },
    },
    subscription: {
      title: 'Your subscription',
      line: '{{plan}} plan, billed {{period}}: {{price}} (without VAT).',
      month: 'monthly',
      year: 'yearly',
      renews: 'It renews on {{date}}.',
      cancels:
        'The subscription is cancelled. You keep the {{plan}} plan until {{date}}; after that you go back to the Free plan without losing your data.',
      pastDue:
        'We could not collect the last payment. Update your payment method so you do not lose the plan.',
      manage: 'Manage subscription',
      manageHint:
        'Change plan, update your card or billing details, or cancel the subscription. A secure Stripe page opens.',
      portalFailed: 'We could not open the subscription management. Try again.',
    },
    return: {
      paid: 'Payment received. Your {{plan}} plan is now active.',
      confirming: 'We are confirming the payment with Stripe. It may take a few seconds.',
      cancelled: 'Nothing has been charged. You can choose a plan whenever you like.',
    },
    summaries: {
      gratis: 'For a first review of your key pages.',
      pro: 'For organisations that need to comply and document it.',
      agencia: 'For agencies that manage their clients’ websites.',
      'a-medida': 'For large volumes and organisations with their own needs.',
      infinita: 'Full access to SiteNorma with no usage limits.',
    },
    features: {
      gratis: [
        'One-page and quick scans',
        'Automatic accessibility against WCAG 2.2 A and AA, on desktop and mobile',
      ],
      pro: [
        'Unlimited websites and users',
        'Full scans on verified websites',
        'Regulatory compliance and guided manual review',
        'PDF reports',
      ],
      agencia: ['Everything in Pro', 'More capacity for teams that manage several websites'],
      'a-medida': ['Everything in Agency', 'Terms tailored to your organisation'],
      infinita: [],
    },
  },
  home: {
    figures: {
      sites: 'Websites',
      verified_one: '{{count}} verified',
      verified_other: '{{count}} verified',
      problems: 'Problem types found',
      problemsHelp: 'What problem types are',
      problemsHelpText:
        'A problem type is a kind of problem (for example, images without alternative text). Affected elements are the specific places where it shows up. It adds up the latest scan of each website, leaving out what was discarded.',
      elements_one: '{{count}} affected element',
      elements_other: '{{count}} affected elements',
      elementsCapped_one: '{{count}}+ affected element',
      elementsCapped_other: '{{count}}+ affected elements',
      problemsHelpCapped:
        'The “+” means the scan stopped counting elements in some check: the real total may be higher.',
      global_one: '{{count}} global',
      global_other: '{{count}} global',
      none: 'No scan yet',
      pages: 'Pages scanned',
      pagesHint: 'Only the latest scan of each website, not this month’s use',
      pagesHintSome: 'Only the latest of {{analysed}} of {{total}} websites, not this month’s use',
    },
    card: {
      pending: 'Waiting to be scanned',
      never: 'Prepare the first scan to see the results.',
      requested: 'Requested on {{date}}',
      attempt: 'Last attempt: {{date}}',
      lastAnalysis: 'Last scan: {{date}}',
      lastCompleted: 'Last completed scan: {{date}}',
      estimate: 'Automated estimate:',
      noEstimate: 'No estimate yet',
      estimateHelp: 'What the automated estimate means',
      estimateHelpText:
        'This is an automatic estimate out of 100. It is not a percentage of compliance and it is not legal conformity: automated tests only detect part of the barriers.',
      estimateMethod: 'How it is calculated',
      deltaUp_one: 'Improved by {{count}} point compared to the previous scan.',
      deltaUp_other: 'Improved by {{count}} points compared to the previous scan.',
      deltaDown_one: 'Worsened by {{count}} point compared to the previous scan.',
      deltaDown_other: 'Worsened by {{count}} points compared to the previous scan.',
      problems_one: '{{count}} problem type',
      problems_other: '{{count}} problem types',
      priority: {
        alta_one: '{{count}} high priority',
        alta_other: '{{count}} high priority',
        media_one: '{{count}} medium priority',
        media_other: '{{count}} medium priority',
        baja_one: '{{count}} low priority',
        baja_other: '{{count}} low priority',
      },
      notAnalysed_one: '{{count}} page could not be scanned.',
      notAnalysed_other: '{{count}} pages could not be scanned.',
      ownership: {
        verified: 'Ownership verified',
        unverified: 'Ownership not verified',
        checking: 'Checking ownership',
        failed: 'Ownership could not be verified',
      },
      actions: {
        prepare: 'Scan my website',
        results: 'View results',
        progress: 'View the scan',
        retry: 'Try the scan again',
        open: 'Open the website',
      },
    },
    subtitle: 'One place to review and improve your websites’ accessibility.',
    listTitle: 'All websites',
    search: 'Search by name or domain',
    results: 'Websites found: {{count}}',
    noResults: 'No matching websites. Try another name or domain.',

    title: 'Your websites',
    organization: 'Organisation: {{name}} · Your role: {{role}}',
    empty: 'You have not added any websites yet.',
    emptyHint: 'Add your first website and start with a quick scan. Verify ownership to unlock full scans.',
    emptyReadOnly: 'When someone in your organisation adds a website, you will see it here.',
    add: 'Add a website',
    roles: { owner: 'Owner', admin: 'Administrator', editor: 'Editor', viewer: 'Viewer' },
  },
  sites: {
    status: {
      verified: 'Ownership verified',
      unverified: 'Ownership not verified',
      checking: 'Checking ownership',
      failed: 'Ownership could not be verified',
    },
    profiles: {
      eaa: 'Private service (European Accessibility Act)',
      'sector-publico': 'Public sector',
      voluntario: 'Voluntary',
      'sin-definir': 'Not defined',
    },
    profileHints: {
      eaa: 'For businesses that offer consumers services covered by the law (e-commerce, banking, transport…). Whether it applies to you depends on your activity and size. Reference: Spanish Law 11/2023.',
      'sector-publico':
        'For public administrations and public sector bodies. Reference: Royal Decree 1112/2018.',
      voluntario: 'For websites with no specific legal obligation: WCAG 2.2 AA is the technical reference.',
    },
    standards: {
      'EN301549-4.1.1': 'EN 301 549 v4.1.1 · WCAG 2.2 AA',
      'EN301549-3.2.1': 'EN 301 549 v3.2.1 · WCAG 2.1 AA',
      'WCAG22-AA': 'WCAG 2.2 AA',
      'WCAG21-AA': 'WCAG 2.1 AA',
    },
  },
  newSite: {
    title: 'Add your website',
    intro:
      'Enter your website’s address to add a website. After that you can start the accessibility analysis.',
    url: 'Website address',
    urlHint:
      'Enter the address of the page you want to analyse first. For example, https://www.yourwebsite.com',
    name: 'Project name (optional)',
    nameHint: 'If you leave it empty, we will use the website’s domain.',
    normalized: 'Address that will be saved: {{url}}',
    existing: 'You already have a website with this address: “{{name}}”.',
    open: 'Open website',
    submit: 'Add website',
    submitting: 'Adding website…',
    cancel: 'Back to your websites',
    errors: {
      url: 'Enter a valid web address, for example, https://www.yourwebsite.com',
      duplicada: 'This website already exists in your organisation.',
      permiso: 'Your role does not allow adding websites.',
    },
  },
  account: {
    menuProfile: 'My profile',
    menuSecurity: 'Security',
    planLabel: '{{plan}} plan',
    nav: 'Your account',
    scope:
      'These settings are personal: they do not depend on any website or on your organisation. Each website’s settings are in the website itself, under “Settings”. The plan and billing are under Organisation → Plan and billing.',
    profile: {
      title: 'My profile',
      name: 'Name',
      email: 'Email address',
      emailHint: 'This is the email you sign in to SiteNorma with. It cannot be changed from here.',
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Changes saved.',
    },
    security: {
      title: 'Security',
      intro:
        'Change the password you sign in with. Doing so signs you out of your other devices; this session continues.',
      current: 'Current password',
      new: 'New password',
      submit: 'Change password',
      submitting: 'Changing…',
      done: 'Your password has been changed. Your other sessions have been closed.',
      currentWrong: 'The current password is not correct.',
    },
  },
  seal: {
    tab: 'Accessibility seal',
    title: 'Accessibility seal',
    intro:
      'Show on your website that you have passed SiteNorma’s accessibility checks. The seal lets people see the results and the date of the last review, but it is not a WCAG certification.', // claims-ok
    status: {
      inactivo: 'Not activated',
      vigente: 'Active',
      caducado: 'Expired',
      suspendido: 'Suspended',
    },
    statusHint: {
      inactivo: 'The seal is not shown on any website.',
      vigente: 'You meet the requirements and the last review is recent.',
      caducado:
        'The last full review is more than {{days}} days old: run a new full scan to renew it. Meanwhile the seal is shown as expired.',
      suspendido:
        'A requirement is no longer met: the seal is shown as suspended and the public page says so.',
    },
    requirements: {
      title: 'Check the requirements',
      progress: '{{done}} of {{total}} requirements completed',
      recommended: 'Recommended (not a requirement)',
      met: 'met',
      pending: 'pending',
      lastAnalysis: 'Last full scan: {{date}} ({{pages}} pages scanned).',
    },
    criteria: {
      ownership: 'The ownership of the website is verified.',
      analysis: 'There is a current full scan, from the last {{days}} days.',
      noHigh: 'There are no pending high-priority problems.',
      manual: 'Every manual check that applies has been reviewed, with no high-priority problems.',
    },
    recommended: {
      statement:
        'The website has a published accessibility statement. It is a document apart from the seal, but we recommend it.',
    },
    fix: {
      ownership: 'Verify the website',
      analysis: 'Run a full scan',
      noHigh: 'See the problems',
      manual: 'Do the manual reviews',
      statement: 'Create the statement',
    },
    scope: {
      title: 'What is checked',
      hint: 'Pages and checks that have to be passed',
      analysis:
        'A full scan of the website (up to 50 pages, on desktop and mobile), finished with every page analysed, from the last {{days}} days.',
      checks:
        'That scan leaves no high-priority problems pending; what the team discarded with a justification does not count.',
      manual:
        'Every manual check that applies to the website, with a recorded review and no high-priority problems.',
      not: 'Passing them is not the same as proving full WCAG compliance: the seal says exactly this and nothing more.',
    },
    activate: {
      title: 'Activate the seal',
      intro:
        'Activating it creates the public verification page and the seal is ready to put on your website. It does not appear on your website until you add it (next step).',
      button: 'Activate the seal',
      disabled_one: '{{count}} requirement is missing',
      disabled_other: '{{count}} requirements are missing',
      done: 'Seal activated. Now put it in your website’s footer.',
      active: 'The seal is activated.',
    },
    withdraw: 'Withdraw the seal',
    withdrawTitle: 'Withdraw the seal?',
    withdrawMessage: 'The seal will stop being shown on your website and the public page will stop working.',
    withdrawn: 'Seal withdrawn.',
    notEligible: 'The website no longer meets all the requirements.',
    readOnly:
      'Your role only lets you view the seal. Ask someone with the editor role or higher to activate it.',
    preview: {
      title: 'Preview',
      notReady_one:
        'It cannot be activated yet: {{count}} requirement is missing. This is how it would look once it is met.',
      notReady_other:
        'It cannot be activated yet: {{count}} requirements are missing. This is how it would look once they are met.',
      language: 'Language',
      detected: '(detected on your website)',
      theme: 'Footer background',
      themes: {
        light: 'Light',
        dark: 'Dark',
      },
      size: 'Size',
      sizes: {
        s: 'Small',
        m: 'Medium',
        l: 'Large',
      },
      states: {
        vigente: 'Active',
        caducado: 'Expired',
        suspendido: 'Suspended',
      },
      footer: 'This is how it looks in a footer',
      footerLinks: 'Legal notice · Privacy · Cookies',
      alt: 'SiteNorma accessibility seal (preview)',
    },
    embed: {
      title: 'Add it to your website’s footer',
      locked: 'Available once you activate the seal.',
      wordpress: {
        title: 'WordPress',
        how: 'In your WordPress, open SiteNorma → Connection and turn on “Show seal on my website”. Choose the size, the language and the light or dark version: the plugin puts it in the footer and only shows it while the seal is active.',
        open: 'Open the plugin in your WordPress',
        none: 'With the SiteNorma plugin the seal goes into the WordPress footer by itself, without touching code.',
        connect: 'Connect the plugin',
      },
      html: {
        title: 'Copy HTML code',
        hint: 'For Shopify, custom-built websites and other systems: the code shows the seal as an SVG image served by SiteNorma, which links to the public verification page.',
        steps: {
          one: 'Choose the language, background and size above.',
          two: 'Copy the code.',
          three:
            'Paste it into the HTML of your website’s or theme’s footer. It needs no scripts and does not change your website’s styles.',
        },
        label: 'HTML code',
        snippetHint:
          'For {{language}}, {{theme}} background and {{size}} size. The image updates by itself if the seal expires or is suspended.',
      },
    },
    verify: {
      title: 'Verify and keep it current',
      intro:
        'Whoever presses the seal reaches a public SiteNorma page with the website’s name and domain, its state, the dates of the last review and of expiry and the scope of what was checked.',
      open: 'Open the public page',
      locked: 'The page is created when you activate the seal.',
      renews: 'It renews by itself when a new full scan meets the requirements.',
      expires: 'It expires {{days}} days after the last full scan if there is no new one.',
      suspends: 'It is suspended if a scan finds that the requirements are no longer met.',
      live: 'The page and the seal’s image show the change at once: a website with an expired or suspended seal does not show it as valid.',
      lastReview: 'Last review: {{date}}.',
      validUntil: 'Valid until {{date}} unless there is a more recent full scan.',
    },
  },
  statement: {
    tab: 'Accessibility statement',
    title: 'Accessibility statement',
    intro:
      'Create the accessibility statement of this website in four steps: choose the languages, fill in the data once, review the document and approve it. With several languages, one form generates them all; on your website the script shows the page language. It is an informative document about the accessibility status, even if it has pending problems. Approving it does not publish it on the website.',
    language: 'Statement languages',
    languages: {
      es: 'Español',
      ca: 'Català',
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
      prompt: 'Choose one or more languages. Those detected on your website are marked.',
      noneDetected:
        'We have not detected the languages of your website yet. Choose which languages you want the statement in.',
      helpLabel: 'Help about the statement languages',
      help: 'Tick every language you want the statement in. The data in the next step is filled in once and generates the text in each language. On your website, one script shows the page language.',
      detected: 'detected',
      needOne: 'Choose at least one language to continue.',
      selectedOne:
        'The statement will be prepared in {{name}}. Tick another language if you want to remove this one; at least one must remain.',
      selectedMany:
        'The statement will be prepared in {{count}} languages ({{list}}). The data is filled in once for all of them.',
      removeApproved:
        'Continuing will delete the approved statement for the languages you removed (including their history).',
      next: 'Next',
      unsupported:
        'There are also pages in these languages, for which we do not have a template yet: {{list}}.',
      updated: 'Languages updated.',
    },
    published: 'version {{n}} validated',
    draft: 'draft',
    none: 'not created',
    cancel: 'Cancel',
    readOnly:
      'Your role only lets you read the statement. Ask someone with the editor role or higher to edit and validate it.',
    errors: {
      incomplete: 'The statement is not complete yet: check what is missing in step 4.',
      permission: 'Your role does not allow changing the statement. Nothing was saved.',
      generic: 'We could not complete the action. Try again; nothing has been published.',
      noWordpress: 'This website does not have the SiteNorma plugin connected in WordPress.',
    },
    steps: {
      language: 'Languages',
      data: 'Fill in the data',
      review: 'Generate and review',
      validate: 'Review and approve',
    },
    status: {
      pendiente: 'Pending validation',
      validada: 'Validated, not yet published on your website',
      publicada: 'Published on your website',
      last: 'version {{n}}, approved on {{date}}',
      changes: 'there are unapproved changes',
    },
    data: {
      title: 'Fill in the data',
      intro:
        'We only ask for what is essential: the details of whoever is responsible for the website. The rest comes from your website, the regulatory profile and the analyses. These data are the same for every language you chose: you do not need to enter them again.',
      entity: 'Responsible entity',
      name: 'Name of the entity',
      address: 'Address',
      email: 'Email',
      emailHint:
        'It is also the accessibility contact email, unless you give another one in the advanced options.',
      website: 'Website',
      prefilled: 'Filled in from your website. You can change them if needed.',
      siteName: 'Website name',
      siteUrl: 'Website address',
      guidedTitle: 'Complaint procedure',
      advanced: 'Advanced options',
      advancedHint: 'Tax ID, phone, contact, scope, exceptions and how it was evaluated',
      taxId: 'Tax ID (optional)',
      phone: 'Phone (optional)',
      contactEmail: 'Accessibility contact email (optional)',
      contactEmailHint: 'If you leave it empty, the entity’s email is used.',
      contactUrl: 'Contact form or page (optional)',
      responseDays: 'Response time in days (optional)',
      scope: 'Scope (optional)',
      scopeHint: 'If the statement does not cover the whole website (a section, a subdomain…), say so here.',
      exceptions: 'Exceptions (optional)',
      exceptionsHint:
        'Content out of scope or disproportionate burden, in your own words and with its justification.',
      enforcement: 'Complaint procedure',
      enforcementHint:
        'Optional. Say to whom and how a complaint can be made, if your entity has a procedure. SiteNorma does not make it up.',
      enforcementRequired:
        'Required in the public sector. State the responsible unit and the complaint procedure that applies to you: SiteNorma does not make it up.',
      preparationNote: 'How it was evaluated, in your words (optional)',
      generate: 'Generate the statement',
      regenerate: 'Generate the statement again',
      generated: 'Statement generated in every chosen language. Review it.',
    },
    evaluation: {
      label: 'Evaluation',
      state: {
        pendiente: 'Pending',
        incompleta: 'Incomplete',
        completada: 'Completed',
      },
      pendingText: 'Run the scan and the manual review to prepare the statement.',
      incompleteBarriers: 'Barriers were found. Complete the manual review to set the conformity.',
      incompleteClear:
        'The automatic scan found no barriers. Complete the manual review to set the conformity.',
      pendingChecks: 'View pending checks',
      completePartial: 'The review justifies partial conformity. The failures are in the statement.',
      completeUndeclared: 'The review does not justify declaring a degree of conformity.',
    },
    document: {
      label: 'Document',
      draft: 'Draft',
      approved: 'Approved',
    },
    evidence: {
      auto: 'Automatic scan',
      autoValue: 'Latest scan on {{date}}: {{pages}} pages scanned and {{problems}} pending problems.',
      noScan: 'There is no finished scan yet. Run one in “Scans” so the statement picks it up.',
      manual: 'Manual review',
      manualValue:
        '{{registered}} of {{total}} criteria reviewed. The rest have not been checked and the statement says so.',
      outdated: 'There is a more recent scan than the one used for this text.',
    },
    review: {
      title: 'Generate and review',
      intro:
        'SiteNorma has generated the statement from your data, the website’s regulatory profile and the technical reference ({{standard}}). This is how it will look on your website.',
      generated: 'Statement updated with the latest scan.',
      update: 'Update with the latest scan',
      previewLabel: 'Statement preview',
      empty: 'There is no statement yet: fill in the data to generate it.',
      readOnly:
        'The text is read-only. If you need to change something, go back to the data: the statement is generated again.',
      edit: 'Edit data',
      continue: 'Review and approve',
    },
    validate: {
      title: 'Review and approve',
      blocked: 'Required information is missing before approval.',
      fixData: 'Fill in the data',
      regime: 'The public sector requires a concrete conformity status.',
      openAnalysis: 'Go to the scan',
      seeText: 'View the statement',
      confirm: 'I have reviewed the document and confirm that the information is correct.',
      confirmPending:
        'I have reviewed the document in the pending languages and confirm that the information is correct.',
      action: 'Approve the document',
      actionPending: 'Approve the pending languages',
      clarification: 'Approving does not certify the accessibility of the website.',
      back: 'Back to the review',
      done: 'Version {{n}} approved in every ready language. Now choose how to publish it.',
      donePending: 'Approved: {{list}}. You can now copy the HTML for each language.',
      pendingLangs_one:
        '{{list}} still needs approval. Until then, the script and the HTML only include the languages already approved.',
      pendingLangs_other:
        '{{list}} still need approval. Until then, the script and the HTML only include the languages already approved.',
      unvalidatedChanges:
        'There are changes after version {{n}}: your website keeps that version until you approve the new one.',
    },
    problems: {
      entity: 'The name of the entity is missing.',
      address: 'The address of the entity is missing.',
      email: 'The email of the entity is missing.',
      site: 'The website address is missing.',
      contact: 'An accessibility contact email is missing.',
      enforcement: 'The complaint procedure is missing (required in the public sector).',
      conformity:
        'The public sector requires a concrete conformity status, and the evaluation does not justify one.',
      blank: 'The text still has data to fill in: go back to the data.',
      empty: 'There is no statement yet: fill in the data to generate it.',
    },
    publication: {
      title: 'Publish on your website',
      intro:
        'Version {{n}} is approved ({{date}}). Approving does not put it on your website: choose a method, follow the steps and mark it when it is on the page. The script and the link always show the latest approved version.',
      choose: 'Choose how to add the statement',
      languageScript:
        'One script covers {{list}}. It shows the page language (lang attribute or path) as soon as it loads, with no second request.',
      languageLink: 'This link is for the statement in {{name}}. Each language has its own.',
      languageHtml:
        'Copy the HTML for each language and paste it on the matching page. Choose the language and press copy.',
      mark: {
        script: 'I have added the script',
        link: 'I have linked the page',
        html: 'I have pasted the HTML',
        wordpress: 'I have asked to publish it in WordPress',
      },
      markedOn: 'Marked as published on {{date}}.',
      saved: {
        script: 'Marked as published with the script.',
        link: 'Marked as published with the link.',
        html: 'Marked as published with the HTML.',
        wordpress: 'WordPress publication request recorded.',
      },
    },
    delivery: {
      recommended: 'Recommended',
      script: 'Code snippet',
      scriptSummary: 'Updates itself when you approve a new version and follows the page language.',
      scriptHint: 'Copy the code. A loading message shows until the text is ready.',
      link: 'Hosted link',
      linkSummary: 'A page hosted by SiteNorma that updates itself when you approve.',
      linkHint: 'Copy this link and use it on your website, for example in the footer.',
      html: 'HTML',
      htmlSummary: 'Copy the HTML by hand. You will need to paste it again if you approve a new version.',
      htmlHint: 'Copy this HTML and insert it on your website page.',
      htmlLangs: 'HTML language',
      htmlNote:
        'This HTML does not update itself: if you approve another version, copy it again. For automatic updates, use the code snippet or the link.',
      wordpress: 'WordPress',
      wordpressSummary: 'The SiteNorma plugin creates or updates the page in your WordPress.',
      wordpressHint:
        'Mark this option and publish from the SiteNorma plugin in WordPress (or wait for the hourly sync).',
      wordpressNote: 'The plugin confirms when the page is published on your website.',
      wordpressConnect: 'Connect the SiteNorma plugin in WordPress to publish the statement from there.',
      wordpressGo: 'Go to Integrations → WordPress',
      stepCopy: 'Step 1: copy this code',
      stepPaste: 'Step 2: paste it on the page of your website where you want the statement to appear.',
      stepCopyLink: 'Step 1: copy this link',
      stepPasteLink: 'Step 2: link to it from your website, for example in the footer.',
      stepCopyHtml: 'Step 1: copy the HTML in {{name}}',
      quality:
        'While the script arrives, “Loading…” appears in the page language. The text of every approved language is inside the script itself, loads asynchronously and adds no dependencies; it uses real headings and lists and inherits your website’s typography and colours. If it could not be shown, a link to the statement remains.',
      copy: 'Copy',
      copied: 'Copied',
    },
    versions: {
      title: 'Versions',
      hint: 'Each validated version is kept with its date and who validated it. You can recover one as a draft to update it.',
      version: 'Version {{n}}',
      by: 'validated by {{name}}',
      view: 'View',
      hide: 'Hide',
      restore: 'Recover as a draft',
      restored: 'Version {{n}} is now the draft. Nothing is published until you validate it.',
      modify: 'Edit the data and create a new version',
    },
  },
  pagesList: {
    title: 'Pages analysed',
    intro: 'The website’s inventory (not only the latest scan): each page shows its latest result.',
    figures: {
      analysed: 'Pages analysed',
      pending: 'With problems pending',
      clean: 'With no problems pending',
    },
    empty: 'There are no pages analysed yet. Start with a scan from “Scans”.',
    notFound: 'This page is no longer in this website’s results.',
    noMatch: 'No page matches what you chose. Change the filter or the search.',
    filter: {
      label: 'Show',
      todas: 'All pages',
      pendientes: 'With problems pending',
      limpias: 'With no problems pending',
    },
    pending_one: '{{count}} problem pending',
    pending_other: '{{count}} problems pending',
    none: 'No problems pending',
    verified_one: '{{count}} correction verified',
    verified_other: '{{count}} corrections verified',
    lastAnalysis: 'Latest analysis: {{date}}',
  },
  nav: {
    label: 'Dashboard',
    sites: 'Websites',
    organization: 'Organisation',
    users: 'Users',
    menu: 'Menu',
    integrations: 'Integrations',
  },
  mcp: {
    title: 'MCP for Cursor and other agents',
    intro:
      'The agent reads your analyses and applies the fixes in your website’s local code. It does not write to the published site: you publish the changes.',
    note: 'The token is personal and acts with your permissions. Do not share it or commit it to the repository. If you lose it, revoke it and create a new one.',
    create: {
      title: 'Create a token',
      name: 'Name (optional)',
      namePlaceholder: 'e.g. Laptop Cursor',
      button: 'Create token',
      working: 'Creating…',
      limit: 'You have reached the maximum of {{max}} active tokens. Revoke one to create another.',
    },
    created: {
      title: 'Link for the agent',
      body: 'Copy it and paste it into the agent chat (Cursor or another). The agent reads the instructions at the link and configures the SiteNorma MCP. The link carries the token, is shown only once and must not be shared or pasted anywhere else.',
      copyLink: 'Copy link',
      linkCopied: 'Link copied.',
      copy: 'Copy token',
      copied: 'Token copied.',
      configTitle: 'Configuration for other agents',
      configHint:
        'The link makes the agent write this configuration. The command is the launcher path on this computer. If that file is missing, the agent will say so.',
      copyConfig: 'Copy configuration',
      configCopied: 'Configuration copied.',
      close: 'I have saved the token',
    },
    steps: {
      title: 'How to use it',
      one: 'Create a token on this page.',
      two: 'Copy the link and paste it into the agent chat. That configures the SiteNorma MCP.',
      three:
        'Open your website’s code (theme or repository) in the editor and ask the agent to fix SiteNorma’s problems.',
      four: 'Publish the changes and, in the dashboard, run “Check the fix” or a new analysis.',
    },
    list: {
      title: 'Active tokens',
      empty: 'There is no MCP token yet.',
      tableLabel: 'MCP tokens (the table scrolls)',
      name: 'Name',
      prefix: 'Token',
      owner: 'Created by',
      created: 'Created',
      lastSeen: 'Last used',
      never: 'None yet',
      actions: 'Actions',
      revoke: 'Revoke',
      revokeTitle: 'Revoke “{{name}}”?',
      revokeBody:
        'Any agent using this token will no longer be able to read your analyses. You can create another whenever you want.',
      revokeConfirm: 'Revoke',
      cancel: 'Cancel',
      revoked: 'The token “{{name}}” has been revoked.',
      usage: '{{mine}} of {{max}} of your tokens active in this organisation.',
    },
    errors: {
      limit: 'You have reached the maximum of active tokens.',
      generic: 'The action could not be completed. Try again.',
    },
  },
  integrations: {
    title: 'Integrations',
    intro:
      'Connect SiteNorma with other platforms and your development environment to review results and fix accessibility problems.',
    nav: 'Integration sections',
    mcp: 'MCP',
    wordpress: 'WordPress',
  },
  wordpress: {
    title: 'WordPress plugin',
    intro:
      'Install the SiteNorma plugin on your WordPress and you will see the results for that website without leaving its dashboard. The plugin only shows the data of the website it is installed on: complete scans and PDF reports are always made here, in the platform.',
    note: 'The results are an automatic estimate: automated tests only detect problems and the rest needs manual review. The plugin shows the same information you see here.',
    download: {
      eyebrow: 'Download',
      title: 'SiteNorma for WordPress',
      meta: 'Version {{version}} · {{size}} · needs WordPress {{wp}} and PHP {{php}}',
      button: 'Download the plugin',
      checksum: 'Check the file’s integrity',
      unavailable: 'The plugin is not available to download yet.',
    },
    steps: {
      title: 'How to install it',
      one: 'Download the ZIP file.',
      two: 'In your WordPress, go to Plugins → Add New → Upload Plugin and choose the ZIP.',
      three:
        'Activate the plugin and press “Connect with SiteNorma”. It brings you here to approve the connection.',
      four: 'Your website appears in the platform, verifies itself and the plugin shows its results.',
    },
    usage: {
      label: 'Websites linked with the plugin',
      of: '{{used}} of {{limit}} websites linked on your plan ({{plan}})',
      unlimited: '{{used}} websites linked (your plan has no limit)',
    },
    list: {
      title: 'Linked websites',
      empty: 'No website is linked with the plugin yet.',
      tableLabel: 'Websites linked with the plugin (the table scrolls)',
      site: 'Website',
      status: 'Ownership',
      verified: 'Verified',
      unverified: 'Not verified',
      versions: 'Versions',
      versionsValue: 'WordPress {{wp}} · plugin {{plugin}}',
      lastSeen: 'Last contact',
      never: 'None yet',
      linkedBy: 'Linked by',
      actions: 'Actions',
      unlink: 'Unlink',
      unlinkTitle: 'Unlink {{name}}?',
      unlinkBody:
        'The plugin on that website will stop showing results as soon as it tries again. The website and its history stay in SiteNorma; you can link it again whenever you like.',
      unlinkConfirm: 'Unlink',
      cancel: 'Cancel',
      unlinked: '{{name}} has been unlinked.',
    },
    tab: {
      name: 'WordPress',
      title: 'WordPress plugin',
      none: 'No WordPress installation is linked to this website.',
      link: 'Manage in Integrations',
      open: 'Open in WordPress',
    },
    connect: {
      title: 'Connect WordPress with SiteNorma',
      intro: 'A WordPress site wants to link to your account. Check which website it is before you approve.',
      site: 'Website being linked',
      organization: 'Organisation',
      account: 'Account',
      scopeTitle: 'What the plugin will be able to do',
      can1: 'Register this website in your organisation and verify that it is yours.',
      can2: 'Show the results of this website in WordPress, and only of this one.',
      can3: 'Ask for a quick scan of this website, within the limits of your plan.',
      cannot:
        'It will not be able to see other websites, change your account or produce PDF reports: that is only done from here.',
      revoke: 'You can unlink it whenever you like from this platform.',
      warning: 'Only approve if you were the one who asked to connect {{host}}.',
      approve: 'Approve the connection',
      working: 'Connecting…',
      cancel: 'Cancel',
      home: 'Go to the dashboard',
      invalid:
        'The connection link is not valid or is incomplete. Go back to the plugin in your WordPress and press “Connect with SiteNorma” again.',
      errors: {
        generic: 'The connection could not be approved. Please try again in a moment.',
        permiso: 'Your role does not allow linking websites. Ask an editor, admin or owner to do it.',
        'limite-webs':
          'You have reached the maximum number of linked websites on your plan. Unlink one or change plan.',
        callback: 'The return address does not belong to that website, so it cannot continue.',
        url: 'That address is not public. Only websites reachable from the Internet can be linked.',
        vinculada:
          'Somebody else in the organisation linked that website: only they or an admin can link it again.',
        conflicto:
          'Another link of this website was being made at the same time. Start again from the plugin.',
        limite: 'Too many attempts. Wait a moment and try again.',
      },
    },
  },
  users: {
    title: 'Users',
    intro:
      'How sign-ups evolve in SiteNorma, every account registered and the plan each one has. Only superadmins see this page.',
    tableLabel: 'Users of the system (the table scrolls)',
    count_one: '{{count}} user',
    count_other: '{{count}} users',
    columns: {
      user: 'User',
      verified: 'Email verified',
      organizations: 'Organisations',
      signedUp: 'Signed up',
      sites: 'Websites',
      scans: 'Scans',
      lastSeen: 'Last activity',
      status: 'Status',
      plan: 'Plan',
    },
    verifiedYes: 'Yes',
    verifiedNo: 'No',
    noOrganizations: 'None',
    neverSeen: 'Never',
    planOf: "{{name}}'s plan",
    limitMonth: '{{count}} pages a month',
    limitNone: 'No limits',
    infinite: 'Turn on the infinite plan',
    infiniteOn: 'Infinite plan on',
    saved: "{{name}}'s plan changed to {{plan}}.",
    error: 'The plan could not be changed. Try again.',
    plans: {
      gratis: 'Free',
      pro: 'Pro',
      agencia: 'Agency',
      'a-medida': 'Custom',
      infinita: 'Infinite',
    },
    wordpress: {
      title: 'WordPress installations',
      intro:
        'Websites linked with the plugin across all accounts. When one is unlinked, the plugin stops showing results on its next request; the website and its history are kept.',
      tableLabel: 'Linked WordPress installations (the table scrolls)',
      empty: 'No installation is linked.',
      organization: 'Organisation',
      unlinkBody:
        'This installation will stop working at once even if the account stays active. It is recorded in the change log.',
    },
    overview: {
      title: 'Sign-ups',
      period: 'Period of the charts',
      lastDays_one: 'Last day',
      lastDays_other: 'Last {{count}} days',
    },
    kpi: {
      accounts: 'Accounts in total',
      last7: 'Sign-ups in 7 days',
      last30: 'Sign-ups in 30 days',
      trend: '{{change}} on the previous 7 days ({{previous}})',
      waitlist: 'Waiting list, 30 days',
      audits: 'Free audits, 30 days',
      total_one: '{{count}} in total',
      total_other: '{{count}} in total',
      blocked: 'Blocked accounts',
    },
    chart: {
      signups: 'Account sign-ups per day',
      signupsColumn: 'Sign-ups',
      waitlist: 'Waiting list sign-ups per day',
      waitlistColumn: 'On the waiting list',
      label: '{{title}}, {{from}} to {{to}}: {{total}} in total, up to {{peak}} in a day.',
      peak: 'Peak: {{count}} in a day',
      data: 'Show the chart data',
      day: 'Day',
    },
    funnel: {
      title: 'What happens after signing up',
      intro: 'Accounts that reached each step, out of all sign-ups.',
      signedUp: 'Signed up',
      verified: 'Email verified',
      organization: 'Belong to an organisation',
      site: 'Added a website',
      scan: 'Launched a scan',
      value: '{{count}} · {{percent}}%',
    },
    filters: {
      search: 'Search by name, email or organisation',
      show: 'Show',
      all: 'All accounts',
      unverified: 'Email not verified',
      noSite: 'No website yet',
      blocked: 'Blocked',
      shown: 'Showing {{count}} of {{total}} users.',
    },
    active: 'Active',
    blocked: 'Blocked',
    administrator: 'Administrator',
    block: 'Block',
    unblock: 'Unblock',
    blockTitle: 'Block {{name}}?',
    blockBody:
      'They will not be able to sign in and the sessions they have open will end. Their data is not deleted and you can unblock the account whenever you like.',
    blockConfirm: 'Block',
    cancel: 'Cancel',
    blockedDone: '{{name}} is blocked.',
    unblockedDone: '{{name}} can sign in again.',
    blockError: 'The status of the account could not be changed. Try again.',
  },
  members: {
    tableLabel: 'People in the organisation (the table scrolls)',
    title: 'Organisation: {{name}}',
    membersTitle: 'People',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    roleOf: 'Role of {{name}}',
    actions: 'Actions',
    you: '(you)',
    remove: 'Remove',
    removeOf: 'Remove {{name}}',
    removeTitle: 'Remove {{name}} from the organisation?',
    removeIntro:
      'They will no longer see the organisation’s websites and scans. You can invite them again later.',
    removeConfirm: 'Remove',
    cancel: 'Cancel',
    roleSaved: 'Role of {{name}} changed to {{role}}.',
    removed: '{{name}} is no longer part of the organisation.',
    add: 'Add someone',
    invitationsTitle: 'Pending invitations',
    invitationItem: '{{email}} · {{role}} · expires on {{date}}',
    cancelInvitation: 'Cancel the invitation',
    cancelInvitationOf: 'Cancel the invitation to {{email}}',
    invitationCancelled: 'Invitation to {{email}} cancelled.',
    inviteTitle: 'Invite someone',
    inviteIntro: 'We will email them a link to join. It expires in 48 hours.',
    inviteEmail: 'Email',
    inviteRole: 'Role',
    inviteSubmit: 'Send invitation',
    invited: 'Invitation sent to {{email}}.',
    roleHints: {
      owner: 'Everything, including deleting the organisation.',
      admin: 'Everything except deleting the organisation: websites, scans and people.',
      editor: 'Add, verify and scan websites, and work on their problems.',
      viewer: 'Read only.',
    },
    errors: {
      generic: 'The change could not be made. Your role may not allow it.',
      already: 'That person is already in the organisation or already has an invitation.',
    },
  },
  report: {
    download: 'Download PDF report',
    working: 'Preparing the report…',
    started: 'Preparing the PDF report. It can take a few seconds.',
    ready: 'Report downloaded.',
    note: 'The PDF report is in Spanish for now, like the texts of the rules.',
    errors: {
      informe: 'The report could not be prepared. Try again in a few minutes.',
      limite: 'You have asked for many reports of this website. Try again in an hour.',
      'sin-terminar': 'The scan has not finished yet.',
    },
  },
  scans: {
    title: 'Scans',
    emptyTitle: 'Scan your website',
    emptyText: 'Choose the type and review the pages before you start.',
    prepareAgain: 'New scan',
    historyCheck: 'Check only',
    prepare: {
      action: 'Scan my website',
      title: 'Prepare your scan',
      loadFailed: 'We could not prepare the summary. Nothing has been started.',
      profile: 'Regulatory profile',
      profileUnset:
        'You have not chosen a regulatory profile yet. Say which rules this scan should take as its reference. You can change it later in the website settings.',
      scopeTitle: 'Type of scan',
      summaryTitle: 'Summary',
      next: 'Next',
      back: 'Back',
      modes: {
        pagina: {
          label: 'One page',
          hint: 'Analyses one page of your website.',
        },
        rapido: {
          label: 'Quick scan',
          hint: 'Analyses up to {{count}} pages of your website.',
        },
        completo: {
          label: 'Full scan',
          hint: 'Analyses up to {{count}} pages of your website.',
        },
      },
      needsOwnership: 'Requires verifying ownership.',
      verifyAction: 'Verify my website',
      checks:
        'We will automatically check accessibility against {{standard}}. Some aspects need a manual review.',
      methodology: 'How we analyse your website',
      analysisLimitTitle: 'Limit per analysis',
      analysisLimit_one: 'Up to {{count}} page with the selected option.',
      analysisLimit_other: 'Up to {{count}} pages with the selected option.',
      analysisLimitExact_one: 'Will use {{count}} page.',
      analysisLimitExact_other: 'Will use {{count}} pages.',
      monthlyQuotaTitle: 'Monthly quota',
      monthlyUsedOf: '{{used}} of {{limit}} pages used.',
      monthlyAvailable_one: '{{count}} page available.',
      monthlyAvailable_other: '{{count}} pages available.',
      monthlyUnlimited: 'No page limit.',
      monthlyAnalysed_one: '{{count}} page analysed this month.',
      monthlyAnalysed_other: '{{count}} pages analysed this month.',
      optionOverQuota:
        'The limit per analysis is {{max}} pages and you have {{remaining}} pages available in the monthly quota.',
      pagesHelp: 'How we count pages',
      pages: {
        title: 'Which pages do you want to analyse?',
        how: 'How they are chosen',
        auto: {
          label: 'Automatic selection',
          hint_one:
            'SiteNorma starts from the home page and picks 1 representative page (sitemap and home links).',
          hint_other:
            'SiteNorma starts from the home page and picks up to {{count}} representative pages (sitemap and home links).',
        },
        manual: {
          label: 'Select pages',
          hint_one: 'Choose 1 address on your website.',
          hint_other: 'Choose up to {{count}} addresses on your website.',
          paidOnly: 'Available on paid plans.',
        },
        autoOne: 'The website’s home page will be analysed: {{url}}.',
        autoMulti:
          'SiteNorma will detect pages on your website and pick up to {{count}} of the most representative ones.',
        search: 'Search pages',
        searchPlaceholder: 'Title or address',
        listLabel: 'Pages detected or analysed before',
        chosen: 'Selected: {{count}} of {{max}}',
        empty: 'No pages match. You can add an address below.',
        addLabel: 'Add an address',
        addHint:
          'It must belong to the same domain as the website. For example, /contact or https://yoursite.com/contact',
        addPlaceholder: 'https://yoursite.com/contact',
        addAction: 'Add to selection',
        addInvalid: 'Enter an address on this website, for example /contact or https://yoursite.com/contact',
        addDuplicate: 'That address is already in the selection.',
        addLimit_one: 'This scan allows at most {{count}} page.',
        addLimit_other: 'This scan allows at most {{count}} pages.',
        summaryTitle: 'Selection summary',
        summaryCount_one: '{{count}} page selected:',
        summaryCount_other: '{{count}} pages selected:',
      },
      noPages: {
        title: 'You have no pages available',
        text: 'You have used this month’s monthly quota. The counter resets on {{date}}.',
      },
      insufficient:
        'You do not have enough pages in the monthly quota: the limit per analysis is {{planned}} and you have {{remaining}} pages available. The counter resets on {{date}}.',
      useOnePage: 'Scan only one page',
      seePlans: 'See plans',
      check: {
        mode: 'Check the fix',
        modeHint_one: 'Only the page where the fix you want to check is, on desktop and on mobile.',
        modeHint_other:
          'Only the {{count}} pages where the fixes you want to check are, on desktop and on mobile.',
        title: 'What will be checked',
        intro:
          'This is a check of fixes, not an analysis of your whole website: it only reviews the pages of these elements and will not look for problems elsewhere.',
        elements_one: '{{count}} element',
        elements_other: '{{count}} elements',
        pagesTitle: 'Pages that will be analysed',
        devices: 'Devices: {{list}}.',
        conditionsTitle: 'Under which conditions',
        conditions:
          'Each page is loaded the way a new visitor sees it, not signed in, on desktop and on mobile. If an element only appears in a pop-up or after signing in, the result will be “Not confirmed automatically” or “Needs manual review”, never “fixed”.',
        scope_one: 'Scope: {{count}} page of the ones your website has.',
        scope_other: 'Scope: {{count}} pages of the ones your website has.',
        skipped_one: '{{count}} element will not be checked (it is archived or discarded).',
        skipped_other: '{{count}} elements will not be checked (they are archived or discarded).',
        tooMany:
          'The fixes are on {{count}} pages and a check can review at most {{max}}. Check fewer elements each time.',
        nothing: 'There is nothing to check with the chosen elements.',
        other: 'Other analyses',
        result:
          'When it ends, each element will show its result: “Still detected”, “Not detected in the latest check”, “Not confirmed automatically” or “Needs manual review”. Only a comparable check that does not detect it allows the work to be archived.',
      },
      start: 'Start scan',
      startAuto_one: 'Analyse 1 page',
      startAuto_other: 'Analyse up to {{count}} pages',
      startExact_one: 'Analyse 1 page',
      startExact_other: 'Analyse {{count}} pages',
      starting: 'Starting scan…',
      cancel: 'Cancel',
      startFailed: 'We could not start the scan. No page has been used: you can try again.',
    },
    errors: {
      'en-curso': 'A scan is already running for this website.',
      verificacion: 'For a full scan, the ownership of the website has to be verified.',
      limite: 'You have run many scans today. Try again tomorrow.',
      saldo: 'You do not have enough pages left this month for this scan. Nothing has been started.',
      permiso: 'Your role does not allow scanning websites.',
      plan: 'Choosing pages by hand is available on paid plans. Nothing has been started.',
      urls: 'An address is invalid, duplicated or does not belong to this website. Nothing has been started.',
      'limite-paginas': 'You have chosen more pages than this type of scan allows. Nothing has been started.',
      'nada-que-comprobar':
        'There is nothing to check: the chosen elements are archived, discarded or no longer exist.',
      'demasiadas-paginas': 'Those fixes are on too many pages for a single check.',
      'no-completable': 'Only a full scan that finished partially can be completed.',
      'nada-que-completar': 'There are no pending pages left to complete in this scan.',
    },
    types: {
      pagina: 'One page',
      rapido: 'Quick',
      completo: 'Full',
      personalizado: 'Custom',
      comprobacion: 'Fix check',
    },
    latestTitle: 'Latest scan',
    lastAvailableTitle: 'Last available result',
    meta: '{{date}} · {{type}}',
    scoreNote: 'Not the legal conformance status.',
    summary: {
      kinds: { rapido: 'quick', completo: 'full', personalizado: 'custom', comprobacion: 'check' },
      pages_one: '{{count}} page',
      pages_other: '{{count}} pages',
      found_one: 'The {{kind}} scan of {{pages}} found {{count}} accessibility problem.',
      found_other: 'The {{kind}} scan of {{pages}} found {{count}} accessibility problems.',
      none: 'The {{kind}} scan of {{pages}} found no accessibility problems with the automated tests.',
      nothing: 'The {{kind}} scan could not analyse any page.',
      pendingReview: 'Some checks are pending manual review.',
    },
    noScore: 'No estimate: no page could be scanned.',
    blocked:
      'The website blocked the automatic scan on {{count}} page(s). If its robots.txt is the cause, verify ownership of the website (scans of verified websites do not depend on it) or allow SiteNormaBot in that file.',
    limited:
      "The website limited SiteNorma's requests on {{count}} page(s): they were not analysed and do not count in the estimate. This is not a defect of the website.",
    partial:
      'On {{count}} page(s) the website limited our requests: the analysis ran on the loaded page, but the screenshot may show images that did not load. We do not count those refusals as problems.',
    details: 'See the full report',
    history: 'Previous scans',
    historyItem: '{{date}} · {{type}}',
    estimate: 'Automated accessibility estimate',
    bands: {
      excelente: 'Excellent',
      bueno: 'Good',
      mejorable: 'Could be better',
      deficiente: 'Poor',
      'revision-pendiente': 'Manual review pending',
      'sin-datos': 'No data',
    },
    modules: { seo: 'SEO', quality: 'Quality' },
    allPages: 'See all {{count}} pages',
    pagesHeading: 'Pages scanned',
    about: 'About this scan',
    seeAllProblems: 'View problems',
    trend: 'Estimate over time',
    historyScore: 'Estimate: {{score}}/100',
  },
  scan: {
    title: 'Scan of {{date}}',
    technical: 'Technical details',
    back: 'Back to Scans',
    pageBlocked: 'Blocked: {{reason}}',
    pageError: 'With an error: {{error}}',
    issuesTitle: 'Problems',
    groups: {
      violations: 'WCAG problems',
      review: 'Need a manual review',
      bestPractices: 'Best practices',
      seo: 'SEO',
      quality: 'Quality',
    },
    empty: 'None.',
    severity: { critica: 'Critical', grave: 'Serious', moderada: 'Moderate', leve: 'Minor' },
    criteria: 'WCAG {{list}}',
    devices: { escritorio: 'desktop', movil: 'mobile' },
    where: 'On {{pages}} page(s) · {{nodes}} element(s) · {{devices}}',
    fix: 'How to fix it',
    examples: 'Examples',
    exampleOn: 'On {{url}} ({{device}})',
    engineScore:
      'Leaving out the detections your team discarded with justification. The engine gave {{score}}/100.',
    discardedNote_one: '{{count}} detection discarded with justification is not counted.',
    discardedNote_other: '{{count}} detections discarded with justification are not counted.',
    discardedMethod: 'How discards are applied',
    groupCount_one: '({{count}} problem · {{rules}} rule)',
    groupCount_other: '({{count}} problems · {{rules}} rules)',
    rule: {
      variant: 'Variant {{index}} of {{total}}',
      where: 'On {{pages}} page(s) · {{nodes}} element(s)',
      wherePages: 'On {{pages}} page(s)',
      onPage_one: '{{count}} element on this page',
      onPage_other: '{{count}} elements on this page',
      onPageAtLeast_one: 'At least {{count}} element on this page',
      onPageAtLeast_other: 'At least {{count}} elements on this page',
      elsewhere_one: 'also on {{count}} more page',
      elsewhere_other: 'also on {{count}} more pages',
      thisPageAndMore_one: 'On this page and {{count}} more',
      thisPageAndMore_other: 'On this page and {{count}} more',
      thisPage: 'On this page',
      variants_one: '{{count}} variant',
      variants_other: '{{count}} variants',
    },
    elementsOnPage_one: 'Its element on this page',
    elementsOnPage_other: 'Its {{count}} elements on this page',
    charts: {
      severity: 'Problems by severity',
      principles: 'Problems by WCAG principle',
      top: 'The most repeated problems',
      topHint: 'Number of pages where each one appears.',
      pageSeverity: 'Problems on this page',
    },
    principles: { 1: 'Perceivable', 2: 'Operable', 3: 'Understandable', 4: 'Robust' },
    map: {
      title: 'Page map',
      hint: 'Every page, with its screenshot, in the colour of its most serious problem. Select it to see its data.',
      noCaptures:
        'The screenshots of this scan are no longer available: only the two latest scans of each website keep theirs.',
    },
    filters: {
      severity: 'Filter by severity',
      search: 'Search for a problem',
      results_one: '{{count}} problem',
      results_other: '{{count}} problems',
    },
    pages: {
      empty: 'No pages scanned yet. Start with a scan from Scans.',
      notReady: 'This scan has not finished yet: its pages will appear when it does.',
      failed: 'This scan could not be completed, so there are no scanned pages to show.',
      none: 'This scan has no pages.',
      list: 'Pages of the scan',
      sort: {
        label: 'Sort pages',
        prioridad: 'By priority',
        nombre: 'By name',
        pendientes: 'By problems pending',
        fecha: 'By date of the latest analysis',
      },
      search: 'Search a page',
      found: 'Pages found: {{count}}',
      newTab: '(opens in a new tab)',
      elements_one: '{{count}} element affected',
      elements_other: '{{count}} elements affected',
      review_one: '{{count}} to review',
      review_other: '{{count}} to review',
      issues_one: 'Problems on this page ({{count}})',
      issues_other: 'Problems on this page ({{count}})',
      issuesHint:
        'Grouped by rule. Open one to see how to fix it and its elements; “Mark” shows them on the screenshot.',
      clean:
        'We found no problems on this page. Automated tests cannot check everything: the manual review is still pending.',
    },
    facts: {
      status: 'Response',
      load: 'Load',
      ttfb: 'First byte',
      bytes: 'Weight',
      requests: 'Requests',
      lang: 'Language',
      features: 'It has: {{list}}.',
    },
    features: { forms: 'forms', video: 'video', audio: 'audio', iframes: 'iframes' },
    pageBlockedShort: 'Blocked',
    pageErrorShort: 'With an error',
    elementOn: 'Element {{number}} ({{device}})',
  },
  viz: {
    ring: {
      none: 'No estimate: no page could be analysed.',
      pendingHint:
        'The {{pending}} pending points are added when the manual review is done. Until then, the estimate cannot exceed {{max}} out of 100.',
      full: 'Automated estimate: {{score}} out of 100. {{earned}} earned, {{lost}} lost and {{pending}} pending manual review.',
      short: 'Automated estimate: {{score}} out of 100.',
      parts: {
        earned: 'Points earned',
        lost: 'Lost',
        manual: 'Manual review',
        pending: 'Awaiting manual review',
      },
    },
    points_one: '{{count}} point',
    points_other: '{{count}} points',
    severities: { critica: 'Critical', grave: 'Serious', moderada: 'Moderate', leve: 'Minor' },
    review: 'To review',
    chips: {
      ok: 'No problems found',
      review_one: '{{count}} to review',
      review_other: '{{count}} to review',
      critica_one: '{{count}} critical problem',
      critica_other: '{{count}} critical problems',
      grave_one: '{{count}} serious problem',
      grave_other: '{{count}} serious problems',
      moderada_one: '{{count}} moderate problem',
      moderada_other: '{{count}} moderate problems',
      leve_one: '{{count}} minor problem',
      leve_other: '{{count}} minor problems',
    },
    module: { label: '{{label}}: {{score}} out of 100', none: '{{label}}: no data' },
    trend: {
      label_one: 'Estimate over {{count}} scan: {{to}} ({{end}}).',
      label_other: 'Estimate over {{count}} scans: from {{from}} ({{start}}) to {{to}} ({{end}}).',
    },
  },
  live: {
    mosaic: 'Pages of the scan',
    analysing: 'Scanning…',
    analysed: 'Scanned',
    waiting: 'Queued',
    moreWaiting_one: 'and {{count}} more queued',
    moreWaiting_other: 'and {{count}} more queued',
    blocked: 'Blocked',
    error: 'With an error',
  },
  results: {
    backToResults: 'Back to the results of the scan',
    backToPages: 'Back to all pages',
    summaries: 'Scan summaries',
    pagesCount_one: '{{count}} page',
    pagesCount_other: '{{count}} pages',
    priority: {
      alta: 'High priority',
      media: 'Medium priority',
      baja: 'Low priority',
      pendiente: 'Priority pending review',
    },
    priorityWhy: {
      alta: 'It can stop some people from using this part of the website.',
      media:
        'It makes the website harder to use for some people, although there is usually another way to carry on.',
      baja: 'It is a minor annoyance or a recommended improvement.',
      pendiente: 'The automated scan could not decide whether this is a problem: a person has to check it.',
    },
    priorityWhyOther: {
      alta: 'It matters for how the website works or how visible it is. It is not an accessibility problem.',
      media: 'It is worth fixing, but it is not urgent. It is not an accessibility problem.',
      baja: 'It is a minor improvement. It is not an accessibility problem.',
    },
    priorityWhyPractice: {
      alta: 'It is an important best practice for people who use assistive technology, although it is not a direct WCAG problem.',
      media: 'It is a recommended best practice, although it is not a direct WCAG problem.',
      baja: 'It is a minor best-practice improvement, not a WCAG problem.',
    },
    why: 'Why:',
    affects: 'It mainly affects people with: {{who}}.',
    origin: {
      automatic: 'Found automatically',
      review: 'Found automatically · needs a manual check',
      manual: 'Detected in manual review',
    },
    actions: { fix: 'See solution', check: 'See and check' },
    card: {
      elements_one: '{{count}} element affected',
      elements_other: '{{count}} elements affected',
      elementsAtLeast_one: '{{count}}+ elements affected',
      elementsAtLeast_other: '{{count}}+ elements affected',
      review_one: '{{count}} element to check',
      review_other: '{{count}} elements to check',
      reviewAtLeast_one: '{{count}}+ elements to check',
      reviewAtLeast_other: '{{count}}+ elements to check',
    },
    more: {
      title: 'Other checks',
      show: 'Show',
    },
    issue: {
      problem: 'Problem',
      noPageData: 'There is no data of this page for this problem.',
      pageOf_one: 'Affected page',
      pageOf_other: 'Affects {{count}} pages: choose one',
      fix: 'How to fix it',
      check: 'How to check it',
      wcag: 'WCAG reference',
      elements: 'Affected elements',
      pages: 'Affected pages',
      technical: 'Technical details',
      moreElements: 'And {{count}} more elements. You will find them in the affected pages.',
      seeInPage: 'See on the page',
      sharedOne: 'This problem affects {{pages}} pages and is in a shared component.',
      sharedMany: 'This problem affects {{pages}} pages and is in {{components}} shared components.',
      seePages_one: 'See the affected page',
      seePages_other: 'See the {{count}} affected pages',
      sharedSelector: 'Selector of the shared component',
      area: 'Area',
      severity: 'Severity',
      type: 'Type',
      showing: 'Showing {{shown}} of {{total}}',
      areas: { todas: 'All', bestPractices: 'Best practices', seo: 'SEO', quality: 'Quality' },
      tracking: {
        title: 'Follow-up of this problem',
        hint: 'Work, validation and verification',
        none: 'Not followed',
        validation: 'Confirmed: {{confirmed}} · Discarded: {{discarded}}',
        noDecisions: 'No decisions',
        verificationTitle: 'Verification of the fix',
        verification: 'Verified: {{verified}} · Still detected: {{persists}} · Pending: {{pending}}',
      },
    },
    shared: {
      chip: 'Global problem',
      title: 'Shared elements',
      intro:
        'The same element, with the same structure, appears on several pages: a shared component (header, footer, menu, form…) causes it. It is one problem, managed from one place and fixed once.',
      zone: 'In {{zone}}',
      pages_one: 'On {{count}} page',
      pages_other: 'On {{count}} pages',
      identical: 'The code is identical on all of them.',
      textChanges:
        'Same structure; the text or the addresses change between pages (for example, between languages).',
      seePages: 'See the pages',
      separately:
        'It is followed and fixed from one place: what you record applies to all the pages, but each one is only taken as verified when a new analysis no longer finds it there.',
      owner: 'Managed from {{page}}',
      ownerGlobal: 'Managed from the global problems section',
      elementIn_other: 'Element shared on {{count}} pages',
      alsoHere_one: 'Another global problem also affects this page; it is managed elsewhere.',
      alsoHere_other: '{{count}} global problems also affect this page; they are managed elsewhere.',
    },
    globals: {
      title: 'Global problems',
      intro:
        'They affect elements shared by several pages (header, footer, menu…). They are shown once and managed from one place.',
      manage: 'View and manage',
      where: 'Managed on {{page}}',
      whereGlobal: 'The home page does not have it: managed on {{page}}',
    },
    counts: {
      pageOnly: 'Affects this page',
      pagesOnly: 'Affects {{pages}}',
      elementsOnly_one: '{{count}} element',
      elementsOnly_other: '{{count}} elements',
      elementsOnlyAtLeast_one: 'at least {{count}} element',
      elementsOnlyAtLeast_other: 'at least {{count}} elements',
      affectsPage: 'affects the page',
      languages_one: '{{count}} language: {{list}}',
      languages_other: '{{count}} languages: {{list}}',
    },
    devices: { both: 'on desktop and mobile', desktop: 'on desktop only', mobile: 'on mobile only' },
    manual: {
      figures: { registered: 'Reviewed', pending: 'Pending', decide: 'Elements to decide' },
      open: 'See the manual checks',
      title: 'Manual checks',
      progress: '{{done}} of {{total}} criteria have a review recorded',
      pending_one: '{{count}} criterion pending manual review.',
      pending_other: '{{count}} criteria pending manual review.',
      itemsDecided:
        'Of {{total}} elements the scan could not decide, your team has confirmed or discarded {{decided}}.',
      hint: 'Automated tests do not replace the manual review: no criterion is treated as met until a person reviews it.',
      intro:
        'The manual review checks, criterion by criterion, what no automated tool can decide. This list holds the criteria of your website’s standard and where each one stands: the result of the automatic analysis and the one of the manual review are kept apart.',
      status: {
        incumple: 'Fails (detected)',
        'sin-incidencias': 'No automated problems: needs manual review',
        pendiente: 'Pending manual review',
      },
      statusHelp: {
        incumple: 'The automated scan found at least one problem that affects this criterion.',
        'sin-incidencias':
          'The automated checks that apply found no problems, but that is not enough: it has to be reviewed by hand.',
        pendiente:
          'No automated test checked it on these pages, or it cannot be checked with automated tools.',
      },
      toReview_one: '{{count}} item to decide by hand',
      toReview_other: '{{count}} items to decide by hand',
    },
    summary: {
      globalProblems_one: '1 affects several pages',
      globalProblems_other: '{{count}} affect several pages',
      title: 'Summary',
      priority: 'Priority',
      priorities: {
        alta: '{{count}} high priority',
        media: '{{count}} medium priority',
        baja: '{{count}} low priority',
      },
      problems: 'Problem types',
      problemsHelp: 'What a problem type is',
      problemsHelpText: 'One kind of failure, even if it repeats on several pages or elements.',
      elements: 'Elements affected',
      elementsHelp: 'What an affected element is',
      elementsHelpText:
        'An image, a button or another element with failures. If it fails on desktop and on mobile, it counts once.',
      atLeast: 'At least',
      pages: 'Pages with problems',
      pagesOfTotal: '{{count}} of {{total}} scanned',
      sharedElements: '{{count}} repeat on other pages',
      repeated:
        'The same element can fail in several problem types, and it is counted once in the total. That is why the sum is {{summed}} and the total is {{total}}.',
      none_one: 'No automated problems were found on the page scanned.',
      none_other: 'No automated problems were found on the {{count}} pages scanned.',
      how: {
        title: 'How these figures are counted',
        problem: {
          term: 'Problem type',
          text: 'One kind of failure (for example, “Image without alternative text”), however many times it repeats.',
        },
        element: {
          term: 'Affected element',
          text: 'An image, a button or another element with failures. If it fails on desktop and on mobile, it counts once.',
        },
        atLeast: {
          term: 'The “+” sign',
          text: 'The scan stops counting when a page has many elements; the real total may be higher.',
        },
        appearance: {
          term: 'Appearance',
          text: 'Every time a check finds an element (per device). Shown in the technical detail.',
        },
        devices: {
          term: 'Devices',
          text: 'The figures add up desktop and mobile, unless a filter says otherwise.',
        },
        pages: {
          term: 'Pages with problems',
          text: 'Pages in this scan with at least one problem type. The “Pages” tab lists every page scanned at some point.',
        },
        pageCount: {
          term: 'The number on each page',
          text: 'Distinct problem types on that page. The pages can add up to more than the scan total.',
        },
        repeated: {
          term: 'One element in several types',
        },
      },
    },
    pageSummary: {
      global_one: 'of them, 1 global',
      global_other: 'of them, {{count}} global',
      title: 'Summary of this page',
      none: 'The automated scan found no accessibility problems on this page. That does not mean it is fully accessible.',
      problems_one: '{{count}} problem detected',
      problems_other: '{{count}} problems detected',
      elements_one: '{{count}}{{plus}} element affected',
      elements_other: '{{count}}{{plus}} elements affected',
      priorities: {
        alta: 'High priority: {{count}}',
        media: 'Medium priority: {{count}}',
        baja: 'Low priority: {{count}}',
      },
    },
    start: {
      title: 'Where to start',
      intro:
        'Start with these problems: they have the highest priority and, among equals, they affect the most elements and pages.',
      counts_one: '{{count}} element affected on {{pages}}',
      counts_other: '{{count}} elements affected on {{pages}}',
      countsAtLeast_one: 'At least {{count}} element affected on {{pages}}',
      countsAtLeast_other: 'At least {{count}} elements affected on {{pages}}',
      how: {
        title: 'How is the priority decided?',
        intro:
          'The priority shows how much a problem hinders the use of the website, according to the impact the test reports. It does not measure legal risk.',
        alta: 'It can stop someone from completing a task (for example, a button a screen reader cannot announce).',
        media: 'It makes use harder, but there is usually another way to carry on.',
        baja: 'A minor annoyance or a recommended improvement.',
        pendiente: 'The scan cannot decide whether it is a problem: a person needs to review it.',
        order:
          'Within each priority, the ones that affect the most elements come first, then the most pages and, last, alphabetical order.',
      },
    },
    score: {
      title: 'Automated estimate',
      value: '{{score}}/100',
      intro: 'From 0 to 100 points. Not a WCAG compliance percentage or legal conformance.',
      method: 'How it is calculated',
    },
    honesty: {
      text: 'Automated analysis finds only some of the problems. Some aspects need a manual review.',
      link: 'How we analyse your website',
    },
    pageCard: {
      onlyGlobal_one: '{{count}} global problem',
      onlyGlobal_other: '{{count}} global problems',
      languages: 'Versions by language',
      showLanguage: 'View the {{language}} version',
      problems_one: '{{count}} problem',
      problems_other: '{{count}} problems',
      none: 'No problems detected',
      noneShort: 'No problems',
    },
    context: {
      label: 'What was scanned',
      date: 'Scan of {{date}}',
      devices: 'Devices: desktop and mobile',
      complete: 'Complete scan',
      partial_one: 'Partial scan: {{count}} page is still to be scanned',
      partial_other: 'Partial scan: {{count}} pages are still to be scanned',
      previous: 'Earlier result',
    },
    previous: {
      title: 'This is an earlier result.',
      text: 'There is a more recent scan, from {{date}}.',
      link: 'See the most recent scan',
    },
    tabs: {
      label: 'Categories of results',
      problems: 'Problems found',
      passed: 'Checks with no problems',
      unchecked: 'What could not be checked',
      manual: 'Manual checks',
      other: 'Best practices, SEO and quality',
    },
    problems: {
      none: {
        scan: 'The automated scan found no accessibility problems on the pages scanned. That does not mean the website is fully accessible: the manual checks are still missing.',
        page: 'The automated scan found no accessibility problems on this page. That does not mean it is fully accessible: the manual checks are still missing.',
      },
    },
    passed: {
      level: 'Level {{level}}',
      result: 'Result:',
      resultPage: 'The automatic check found no problems on this page.',
      resultPages_one: 'The automatic check found no problems on {{count}} page.',
      resultPages_other: 'The automatic check found no problems on {{count}} pages.',
      where:
        'It looks at the whole page: the scan does not keep the specific elements it checked, so they cannot be marked on the screenshot. There is nothing to fix here, and it does not mean the criterion is fully met: a manual review is needed.',
      intro: {
        scan: 'Automated checks that applied to the pages scanned and found no problems.',
        page: 'Automated checks that applied to this page and found no problems.',
      },
      note: 'A check finding no problems does not mean the criterion is met: automated tests only detect some of the barriers, and the criteria are still pending manual review.',
      unavailable:
        'This scan was run before we saved this information. Run a new scan to see the checks with no problems.',
      none: 'No check could be applied without finding problems.',
      wcag_one: '{{count}} check from the WCAG guidelines',
      wcag_other: '{{count}} checks from the WCAG guidelines',
      practices_one: '{{count}} best-practice check',
      practices_other: '{{count}} best-practice checks',
      inPages_one: 'on {{count}} page',
      inPages_other: 'on {{count}} pages',
    },
    unchecked: {
      intro:
        'Here is what the automated scan could not decide and what was not scanned. None of this is treated as fine.',
      byHand: 'To decide by hand ({{count}})',
      byHandHint:
        'The automated scan could not decide whether this is a problem. A person has to check it and, if they wish, record their decision.',
      noneByHand: 'Nothing is waiting to be decided by hand.',
      pages: 'Pages that were not scanned ({{count}})',
      pagesHint: 'These pages were not scanned, so they are not treated as passed.',
      noPages: 'All the planned pages were scanned.',
    },
    other: {
      intro: 'Checks that help improve the website but are not WCAG accessibility problems.',
      note: 'Best practices, SEO and technical quality do not count in the summary of accessibility problems.',
      bestPractices: 'Accessibility best practices',
      bestPracticesHint:
        'They improve the experience with assistive technology, but they do not directly fail a WCAG criterion.',
      seo: 'SEO (visibility in search engines)',
      seoHint: 'They help search engines find and understand the website.',
      quality: 'Technical quality',
      qualityHint: 'Technical errors, security and cookies of the page.',
      empty: 'Nothing to show here.',
    },
    filters: {
      title: 'Filter',
      priority: 'Priority',
      device: 'Device',
      devices: { todos: 'All devices', escritorio: 'Desktop', movil: 'Mobile' },
      search: 'Search by name, explanation or WCAG criterion',
      shown: 'Showing {{shown}} of {{total}}.',
      clear: 'Clear filters',
      noMatch: 'Nothing matches the filters. Press “Clear filters” to see everything.',
    },
    detail: {
      title: 'Detailed results',
      howToCheck: 'How to check it',
      where: 'Where it is',
      examples: 'Examples of elements',
      engineMessage: 'Engine technical message',
      elementsHere_one: 'The element on this page',
      elementsHere_other: 'The {{count}} elements on this page',
      technical: 'Technical detail',
      rule: 'Engine rule',
      criteria: 'Related WCAG criteria',
      criterion: 'WCAG {{id}} {{title}} (level {{level}})',
      appearances: 'Appearances',
      appearancesValue_one: '{{count}} appearance',
      appearancesValue_other: '{{count}} appearances',
      appearancesHelp:
        'Each time the check finds an element it counts one appearance: the same element failing on desktop and on mobile adds two appearances, but it is a single element.',
      groups: 'Technical groups',
      groupsValue_one: '{{count}} group',
      groupsValue_other: '{{count}} groups',
      groupsHelp:
        'Elements of the same rule grouped because they are the same kind of element in the same page structure, a relation the engine checks. Each group has its own follow-up.',
      scopeNote: 'What is counted here',
      scopeNoteValue: 'Distinct elements across all the pages scanned, desktop and mobile together.',
    },
  },
  status: {
    outcomes: {
      queued: 'Scan queued',
      running: 'Scan in progress',
      complete: 'Scan completed',
      partial: 'Scan partially completed',
      failed: 'The scan could not be completed',
    },
    announce: {
      queued: 'Scan queued.',
      running: 'Scan in progress.',
      complete: 'Scan completed. You can now see the results.',
      partial: 'Scan partially completed. Check which pages were not scanned.',
      failed: 'The scan could not be completed.',
      refreshed: 'Status updated.',
    },
    identity: {
      url: 'Address',
      scope: 'Scope',
      scopeValue_one: '{{mode}} · up to {{count}} page, on desktop and mobile',
      scopeValue_other: '{{mode}} · up to {{count}} pages, on desktop and mobile',
      requested: 'Requested on',
    },
    modes: {
      pagina: 'Only the page you entered',
      rapido: 'Quick scan',
      completo: 'Full scan',
      personalizado: 'Custom scan',
      comprobacion: 'Fix check',
    },
    queued: { text: 'Your scan is queued. It will start when capacity is available.' },
    leave: 'You can leave this screen. The scan will carry on and you can check its status from the website.',
    progress: 'Scanning {{done}}/{{total}}',
    progressEta: {
      seconds: 'About {{count}} seconds left',
      minutes_one: 'About {{count}} minute left',
      minutes_other: 'About {{count}} minutes left',
    },
    phases: {
      title: 'Scan phases',
      queued: 'Queued',
      preparing: 'Preparing the scan',
      analysing: 'Scanning the pages',
      finishing: 'Working out the estimate',
      done: 'completed',
      current: 'current phase',
    },
    refresh: {
      failed: 'We could not update the status. Try again.',
      action: 'Update status',
      lastKnown: 'Last status received at {{time}}. The scan has not stopped because of this.',
    },
    partial: {
      text: '{{done}} of {{total}} pages scanned.',
      pending_one: '1 page missing: it is not treated as passed.',
      pending_other: '{{count}} pages missing: they are not treated as passed.',
      why: 'The scan could not finish fully: at least one page was not scanned (for example, because it ran out of time). Each one is listed below with the reason.',
      next: 'Run the scan again to try to cover the missing pages.',
      nextFull: 'This result does not count as a current full scan. Run a full scan again.',
      nextFullComplete:
        'This result does not count as a current full scan. You can complete only the pending pages: those are re-scanned and the ones that already succeeded are kept. Only the pages scanned again count towards your balance.',
      complete_one: 'Complete 1 pending page',
      complete_other: 'Complete {{count}} pending pages',
      completing: 'Completing scan…',
      completeFailed: 'We could not complete the scan. No pages were used: you can try again.',
    },
    pending: {
      title: 'Pages not scanned',
      hint: 'They were not scanned and do not count as passed.',
      more: 'And {{count}} more.',
    },
    causes: {
      interrupted: 'The scan was interrupted before it finished and left no results.',
      domain:
        'The website’s domain does not resolve to a public address. Check that the address is correct and that the website is online.',
      limited:
        'The website limited automated requests (HTTP 429) and no page could be scanned. This is not a problem with your website.',
      blocked:
        'No page could be scanned: the automated scan could not reach it. The reason for each one is below.',
      unreachable: 'No page of the website could be loaded.',
      unknown: 'We do not know the exact cause.',
      technical: 'Technical detail',
    },
    consumption: {
      reserved_one:
        'While it runs, up to {{count}} page of your balance is set aside. When it ends, only the pages scanned are counted.',
      reserved_other:
        'While it runs, up to {{count}} pages of your balance are set aside. When it ends, only the pages scanned are counted.',
      used_one: 'Usage of this scan: {{count}} page.',
      used_other: 'Usage of this scan: {{count}} pages.',
      none: 'Usage of this scan: no pages.',
      notCounted_one: 'The page that was not scanned was not counted.',
      notCounted_other: 'The {{count}} pages that were not scanned were not counted.',
    },
    actions: {
      settings: 'Review settings',
      prepare: 'New scan',
      previousResults: 'See the results of that scan',
      seeResults: 'View results',
    },
  },
  compare: {
    title: 'Compared with the scan of {{date}}',
    same: 'Same estimate',
    points_one: '{{signed}} point',
    points_other: '{{signed}} points',
    resolved_one: '{{count}} problem resolved',
    resolved_other: '{{count}} problems resolved',
    new_one: '{{count}} new problem',
    new_other: '{{count}} new problems',
  },
  check: {
    title: 'Latest fix check',
    titleNote: 'Not an analysis of the whole website',
    scope:
      'A check only analyses the pages of the elements being checked: it is not an analysis of the whole website, so it does not work out the estimate and is not compared with earlier analyses.',
    looked: 'Checked {{elements}} on {{pages}}.',
    elementsCount_one: '{{count}} element',
    elementsCount_other: '{{count}} elements',
    pagesCount_one: '{{count}} page',
    pagesCount_other: '{{count}} pages',
    pending: 'Not confirmed automatically',
    summaryPending_one:
      '{{count}} element could not be confirmed automatically. Its selector no longer fails, but it needs a manual review.',
    summaryPending_other:
      '{{count}} elements could not be confirmed automatically. Their selectors no longer fail, but they need a manual review.',
    summaryPossibleChange_one:
      'The element no longer fails with its original selector, but the page still has other problems of the same kind. The scan cannot be sure it is the same element: review it by hand.',
    summaryPossibleChange_other:
      '{{count}} elements no longer fail with their original selector, but the page still has other problems of the same kind. The scan cannot be sure they are the same: review them by hand.',
    summaryPersists_one: '{{count}} element is still detected after the check.',
    summaryPersists_other: '{{count}} elements are still detected after the check.',
    summaryVerified: 'The elements checked are no longer detected in this check.',
    summaryMixed:
      '{{verified}} not detected · {{persists}} still detected · {{pending}} not confirmed automatically.',
    nextPending: 'Review in the follow-up',
    nextPersists: 'See the follow-up',
    nextVerified: 'See the follow-up',
    details: 'See the detail of each element',
    seeResults: 'See the pages checked',
    seeFollow: 'See the follow-up',
  },
  figures: {
    pages: 'Pages scanned',
    failures: 'WCAG problems',
    elements: 'Elements affected',
    review: 'Need a manual review',
  },
  inspector: {
    title: 'Screenshot of the page',
    device: 'Device of the screenshot',
    devices: { escritorio: 'Desktop', movil: 'Mobile' },
    frame: 'Screenshot of the page (it scrolls and zooms)',
    alt: 'Screenshot of {{page}} on {{device}}',
    zoom: { label: 'Enlarge the capture', enlarge: 'Enlarge' },
    reload: 'Reload capture',
    loading: 'Loading the screenshot…',
    loadError: 'The screenshot could not be loaded. Elements are not marked until it shows.',
    retry: 'Try again',
    panHint: 'Drag the screenshot to move around, or focus it and use the arrow keys.',
    notLocated: {
      'no-capture': 'There is no screenshot for this device, so the element cannot be marked.',
      'no-position':
        'This element could not be placed on the capture (it may be inside a frame, hidden or outside the visible part). Its selector is under «See technical solution».',
      outside:
        'This element is outside the part of the page that was captured. See its description in the guide.',
      'other-device': 'This element was not found on this device. Switch device to see it marked.',
    },
    noCapture: 'There is no screenshot of this page on this device.',
    expired:
      'The screenshot is no longer available: only those of the latest two scans of each website are kept.',
  },
  element: {
    guideTitle: 'Guide for {{name}}',
    shared: {
      title_one: 'This element is shared on {{count}} page',
      title_other: 'This element is shared on {{count}} pages',
      here: 'It is managed from this page. What you record here applies to all {{count}} pages.',
      there: 'It is managed from {{page}}. What you record here also applies to all {{count}} pages.',
      open: 'Open on {{page}}',
      identical: 'The code is identical on all of them.',
      textChanges:
        'Same structure; the text or the addresses change between pages (for example, between languages).',
      progress: {
        'sin-aplicar': 'Fix not applied on any page.',
        'aplicada-en-parte': 'Fix applied on only some of the pages.',
        aplicada: 'Fix applied on all pages: it still has to be verified.',
        'verificada-en-parte': 'Fix verified on only some of the pages: it is not taken as resolved.',
        verificada: 'Fix verified on all pages.',
      },
      separately:
        'Marking as applied does not verify the fix: only a new analysis or a manual review confirms it.',
    },
    panel: {
      back: 'All problems',
      problem: 'Problem',
      solution: 'Solution',
      showWhere: 'See where it is',
      backToSolution: 'Back to the solution',
      captureTitle: 'Where the element is',
      element: 'Affected element',
      verifyAuto: 'After you fix it, a new scan will check it automatically.',
      verifyManual:
        'Needs a manual review: the automatic scan cannot decide it and a person has to check it.',
      technical: 'See technical solution',
      wcag: 'WCAG references',
      measured: 'Measured',
    },
    nav: {
      label: 'Elements of the problem on this page',
      position: 'Element {{index}} of {{total}} on this page',
      previous: 'Previous',
      next: 'Next',
      status: 'Element {{index}} of {{total}} on this page: {{name}}.',
      sample: 'Showing {{shown}} of {{total}} elements: where the others are and their code are not kept.',
      analysisTotal_one: '{{count}} across the whole scan',
      analysisTotal_other: '{{count}} across the whole scan',
      noElements:
        'This problem affects the whole page, not an element that can be pointed at. Follow the steps above.',
    },
    sections: {
      owner: 'Who can do it',
      check: 'How to check it',
    },
    where: {
      positionValue: '{{vertical}}% down from the top of the page, {{side}}',
      positionSide: 'On the page, {{side}}',
      sides: { left: 'on the left', center: 'in the middle', right: 'on the right' },
    },
    roles: {
      desarrollo: 'Whoever develops or maintains the website’s code',
      diseno: 'Whoever designs or lays out the website',
      contenido: 'Whoever edits the website’s content (texts, images, links)',
      unknown: 'Whoever maintains the website',
      note: 'This depends on the kind of problem. We do not know who maintains your website or what permissions you have: if you cannot do it yourself, copy the instructions for whoever does.',
    },
    generic: {
      check1: 'Apply the change on your website and reload the page.',
      check2: 'Check that this element no longer has the problem described.',
      check3:
        'Run a new scan of the page to confirm that SiteNorma no longer finds it. Copying or applying a change does not mean the problem is fixed.',
    },
    proposal: {
      adapted: 'Proposal adapted to this element',
      example: 'Example that needs adapting',
      currentCode: 'Current code',
      proposedCode: 'Proposed change ({{language}})',
      copy: 'Copy code',
      copied: 'Code copied.',
      failed: 'It could not be copied. Select the text and copy it by hand.',
      whereKnown: 'Where to apply it: {{where}}.',
      whereUnknown:
        'We do not know which file or template of your website this change goes in: look for it or pass it to whoever maintains the website.',
      notAGuarantee:
        'It is a proposal, not a guaranteed fix: until a new scan confirms it, the problem stays open.',
    },
    decision: { choose: 'Choose an option to see the proposal.' },
    verify: {
      title: 'Check the fix',
      text: 'Copying the code, applying a change or changing the status here does not mean the problem is fixed. Confirming it takes a check: a new analysis (it uses pages of your balance and always goes through the summary first) or a manual review.',
      action: 'Check the fix',
    },
    technical: {
      selector: 'Selector',
      location: 'Technical location',
      position: 'Position',
      xpath: 'XPath',
      code: 'Current code',
    },
    copy: {
      action: 'Copy instructions for the developer',
      copied: 'Instructions copied.',
      failed: 'They could not be copied. Select the text of the guide and copy it by hand.',
      title: 'SiteNorma · Instructions to fix an accessibility problem',
      problem: 'Problem',
      what: 'What is happening',
      where: 'Where it is',
      element: 'Element',
      page: 'Page',
      device: 'Device',
      position: 'Approximate position',
      date: 'Date of the scan',
      evidence: 'Evidence',
      how: 'How to fix it',
      currentCode: 'Current code',
      check: 'How to check it',
      selector: 'Selector',
      note: 'Copying these instructions or applying the change does not mean the problem is fixed: only a new SiteNorma scan confirms it. The automated scan only detects some of the accessibility barriers.',
    },
  },
  manual: {
    title: 'Manual checks',
    intro:
      'Some accessibility aspects need a review by a person. Follow the instructions and record what you have checked.',
    available:
      'You can start a review whenever you like: there is no need to wait until the problems found by the automatic analysis are fixed, and an automatic analysis with no detections does not close a pending review.',
    notAudit:
      'A guide helps you check one aspect on one page. It is not an assessment of your whole website, and recording a review does not mean your website complies with any regulation.',
    readOnly: 'Your role only lets you view the reviews.',
    progress: {
      title: 'Manual review progress',
      summary: '{{registered}} of {{total}} criteria have a review recorded',
      text: 'In the bar, {{registered}} of {{total}} criteria have a review recorded.',
      started_one: '{{count}} more criterion has a draft that is not recorded.',
      started_other: '{{count}} more criteria have a draft that is not recorded.',
      needNew_one: '{{count}} review needs a new review because the page has changed.',
      needNew_other: '{{count}} reviews need a new review because the page has changed.',
      answers: 'Current answers',
      note: 'This progress covers the manual reviews only: it is kept apart from the automatic analysis and from the work on the problems. A recorded review confirms that it was done, not that it was passed, and reviewing a criterion on one page says nothing about the rest of your website.',
    },
    state: {
      'no-iniciada': 'Manual review not started',
      pendiente: 'Pending review',
      registrada: 'Manual review with an answer',
    },
    answer: {
      'sin-problemas': 'I have not found any problems',
      problema: 'I have found a problem',
      'no-comprobado': 'I could not check it',
      'no-aplica': 'Not applicable',
    },
    condition: {
      teclado: 'With the keyboard only',
      'lector-de-pantalla': 'With a screen reader',
      zoom: 'With zoom or enlarged text',
      'espaciado-de-texto': 'With the text spacing changed',
      movil: 'On a phone or with a narrow screen',
      'colores-forzados': 'With forced colours or high contrast',
      'escala-de-grises': 'In greyscale',
      'sin-estilos': 'Without styles (no CSS)',
      'sin-sonido': 'Without sound',
      'reducir-movimiento': 'With “reduce motion” turned on',
      voz: 'With voice control',
    },
    answerHint: {
      'sin-problemas':
        'You followed the guide and everything that should happen does happen. What you reviewed has been checked, not the rest of the website.',
      problema: 'Something that should happen does not. Describe what you saw: a problem will be created.',
      'no-comprobado':
        'You could not do the check (a tool was missing, a page would not load…). It is still unchecked: it is not a favourable answer.',
      'no-aplica':
        'The page does not have what the guide checks (for example, there are no videos). It has to be justified.',
    },
    help: {
      guiada: 'Guided review',
      especializada: 'May require specialist help',
      none: 'No guide available',
    },
    noGuide:
      'There is no guide available for this aspect, so it cannot be checked by following steps from here. It stays pending review.',
    needsNew: 'Needs a new review',
    needsNewHint:
      'The page has changed since it was reviewed. The earlier valuation stays until the review is repeated.',
    row: {
      automatic: 'Automatic analysis',
      manual: 'Manual review',
      pages_one: 'on {{count}} page',
      pages_other: 'on {{count}} pages',
      reviews_one: '{{count}} review recorded',
      reviews_other: '{{count}} reviews recorded',
      drafts_one: '{{count}} draft',
      drafts_other: '{{count}} drafts',
      by: '{{name}}, {{date}}',
      someone: 'a person in your organisation',
    },
    actions: {
      start: 'Start review',
      resume: 'Continue review',
      view: 'See the reviews and repeat',
      repeat: 'Repeat the review',
      guide: 'See the guide',
      back: 'Back to the results',
    },
    page: {
      title: 'Manual check: {{id}} {{title}}',
      level: 'Level {{level}}',
      principle: '{{name}}',
      siteScope:
        'This check compares several pages, so it is recorded for the whole website and not for one page.',
      noPages:
        'There are no analysed pages yet, and this check is done on a page. Run an analysis and come back.',
      loadFailed: 'The guide could not be loaded. Please try again.',
    },
    guide: {
      what: 'What you are going to check',
      how: 'How to do it',
      expected: 'What should happen',
      record: 'How to record the result',
      recordAnswers: 'Choose the answer that fits:',
      specialist: 'Why specialist help may be needed',
      version: 'Guide version: {{version}}.',
    },
    form: {
      title: 'Your review',
      identify: 'What you are reviewing',
      page: 'Page',
      pageHelp: 'The page you reviewed, from those SiteNorma analysed.',
      pagePlaceholder: 'Choose a page',
      whole: 'The whole website',
      element: 'Element',
      elementHelp: 'Optional: the part of the page you reviewed, for example “Main menu”.',
      device: 'Device',
      devices: { ambos: 'Desktop and mobile', escritorio: 'Desktop', movil: 'Mobile' },
      conditions: 'Conditions of the check',
      conditionsHelp: 'Tick what you used to check it.',
      conditionNotes: 'Other conditions',
      conditionNotesHelp: 'Browser and version, screen reader, window size…',
      answer: 'Result of the check',
      answerHelp: 'If you do not choose an answer, the review stays pending.',
      description: 'What you found',
      descriptionHelp: 'Required if you found a problem: what fails, where and how it shows.',
      reason: 'Why you could not check it',
      reasonHelp: 'Optional, but it helps whoever takes it up next.',
      justification: 'Why it is not applicable',
      justificationHelp:
        'Required: explain why this page does not have what is checked (at least 20 characters).',
      severity: 'How much it hinders using the website',
      severities: {
        grave: 'It stops a task from being completed',
        moderada: 'It makes it harder, but it can be completed',
        leve: 'It is annoying, but it does not stop anyone',
      },
      severityHelp: 'It gives the problem its priority, like the problems of the automatic analysis.',
      evidence: 'Evidence',
      evidenceHelp: 'What you saw, the tools and versions you used, a link to a screenshot or a recording.',
      save: 'Save draft',
      saving: 'Saving…',
      register: 'Record review',
      registering: 'Recording…',
      discard: 'Discard draft',
      discardConfirm: 'Discard the draft',
      discardBody: 'What you wrote in this draft is deleted. Recorded reviews are not touched.',
      cancel: 'Cancel',
    },
    saved: {
      draft: 'Draft saved. The review is still pending: there is no recorded answer yet.',
      registered:
        'Manual review recorded. This confirms that it was saved; it does not mean the check was passed.',
      discarded: 'Draft discarded.',
    },
    errors: {
      respuesta: 'Choose an answer to record the review, or save a draft.',
      pagina: 'Choose the page you reviewed.',
      descripcion: 'Describe the problem you found (at least 10 characters).',
      gravedad: 'Say how much it hinders using the website.',
      justificacion: 'Explain why it is not applicable (at least 20 characters).',
      registrada: 'This review is already recorded and cannot be changed. Repeat it to add a new one.',
      permiso: 'Your role does not let you record reviews.',
      'sin-guia': 'There is no guide for this aspect.',
      datos: 'Check the data in the form.',
      generic: 'It could not be saved. Please try again.',
    },
    drafts: {
      title: 'Your drafts in progress',
      intro: 'Pick them up where you left off.',
      open: 'Continue this draft',
      updated: 'Saved on {{date}}',
      start: 'Start a new review',
    },
    history: {
      title: 'Earlier reviews',
      intro:
        'All of them are kept, with the instructions that were followed and their evidence: a new review is added to the earlier ones and does not replace them.',
      empty: 'There is no recorded review of this aspect yet.',
      current: 'Current valuation',
      earlier: 'Earlier',
      by: 'Recorded by {{name}}, {{date}}',
      page: 'Page',
      element: 'Element',
      device: 'Device',
      conditions: 'Conditions',
      none: 'Not stated',
      description: 'What was found',
      reason: 'Why it could not be checked',
      justification: 'Why it is not applicable',
      evidence: 'Evidence',
      severity: 'How much it hinders',
      instructions: 'Instructions that were followed (version {{version}})',
      afterFix: 'Done to check a fix',
      repeats: 'Repeats an earlier review',
    },
    found: {
      origin: 'Detected in manual review',
      from: 'Manual review of criterion {{id}}',
      by: 'Reviewed by {{name}}, {{date}}',
      element: 'Element: {{element}}',
      device: 'Device: {{device}}',
      conditions: 'Conditions: {{list}}',
      evidence: 'Evidence: {{text}}',
      open: 'See the guide and repeat the review',
      note: 'This problem comes from a manual review: it is closed by a later review that does not find the problem, not by an automatic analysis.',
    },
    follow: {
      title: 'Guided manual check',
      hint: 'When the automatic analysis cannot decide, a person can repeat the check by following the guide. A favourable review that covers every device where it was detected allows the work to be archived.',
      repeat: 'Repeat the guide “{{title}}”',
      repeatId: 'Repeat the guide of criterion {{id}}',
    },
    prepare: {
      title: 'Checks that need a person',
      intro:
        'For these elements the automatic analysis cannot decide by itself. You can repeat the check with the guide; the analysis does not close it.',
      repeat: 'Repeat guide {{id}} for {{element}}',
    },
  },
  follow: {
    panel: {
      intro:
        'The status shows the progress of the fix. To confirm the problem is solved, it has to be verified.',
      kinds: {
        'sin-comprobar': 'Not verified',
        pendiente: 'Verification pending',
        verificado: 'Verified',
        persiste: 'The problem is still detected',
        descartada: 'Detection discarded',
      },
      sources: { automatica: 'Automatic analysis', manual: 'Manual check' },
      on: '{{source}} · {{date}}',
      hints: {
        pendiente: 'Analyse the page again to check it.',
        persiste: 'Review the fix and analyse the page again.',
        descartada: 'This detection does not count as a problem.',
      },
      manual: 'The automatic analysis cannot decide this case. It needs a manual check.',
      recheck: 'Analyse again',
      check: 'Check the fix',
      advanced: 'Advanced options',
      advancedHint: 'Validation, discarding, manual check, history and changes to several elements',
      bulk: {
        title: 'Apply status to the {{count}} elements',
        hint: 'Select the status you want to apply.',
        choose: 'Choose a status',
        apply: 'Apply',
        confirmTitle: 'Apply the status to {{count}} elements?',
        confirmBody:
          'This changes the work status of {{count}} elements. Verification results are not modified and each element keeps its own history.',
        confirm: 'Apply status',
        cancel: 'Cancel',
        done: 'Status applied to {{count}} elements.',
        label: 'Status to apply',
      },
    },
    quick: {
      work: 'Work status',
      verification: 'Verification',
      more: 'More tracking options',
    },
    title: 'Problem status',
    save: 'Save status',
    saving: 'Saving…',
    notice:
      'Changing the status in SiteNorma does not change your website. Apply the fix before checking it.',
    readOnly: 'Your role only lets you view the status.',
    saved: 'Status saved.',
    savedMany_one:
      'Status saved on {{count}} element, which keeps its own history. No analysis was started and no page was used.',
    savedMany_other:
      'Status saved on {{count}} elements, each with its own history. No analysis was started and no page was used.',
    errors: {
      justificacion: 'To discard a detection, explain why: at least 20 characters.',
      evidencia: 'To discard a detection, give the evidence behind it: at least 10 characters.',
      permiso: 'Your role does not let you change the status.',
      'permiso-descartar': 'Only the owner and administrators can discard a detection.',
      vacio: 'Choose what you want to change before saving.',
      demasiados: 'That is too many elements at once. Do it one problem at a time.',
      generic: 'The status could not be saved. Please try again.',
    },
    by: '{{name}}, {{date}}',
    someone: 'a person in your organisation',
    system: 'an analysis',
    noteLabel: 'Note',
    noteHint: 'Optional. It stays in the history with your name and the date.',
    work: {
      title: 'Work tracking',
      description: 'What your team is doing. It does not say whether the problem is solved.',
      statuses: {
        pendiente: 'Pending to fix',
        'en-curso': 'In progress',
        aplicada: 'Fix applied',
        aplazada: 'Postponed',
        archivada: 'Work archived',
      },
      hints: {
        pendiente: 'Nobody has started fixing it.',
        'en-curso': 'Someone on the team is working on it.',
        aplicada: 'You have changed the website. It still needs verifying: only a check can confirm it.',
        aplazada: 'It will be fixed later. The problem stays in the results and in the score.',
      },
      reopens: 'Choosing a status reopens archived work.',
    },
    validation: {
      title: 'Detection validation',
      description: 'Whether the detection is a real problem. It does not say whether it is fixed.',
      none: 'Automatic detection, no manual validation',
      statuses: {
        pendiente: 'Pending validation',
        confirmada: 'Detection confirmed',
        descartada: 'Detection discarded with justification',
      },
      hints: {
        pendiente: 'It needs a manual review before deciding whether it is a problem.',
        confirmada: 'A person has checked that it is a real problem. It does not mean it is fixed.',
        descartada:
          'It is not a real problem on this website. It leaves the counts and the score, and is listed apart with your justification.',
        revert: 'Removes the earlier decision and validates the detection again.',
      },
      keep: 'No change',
      adminOnly:
        'Discarding a detection changes the counts and the score: only the owner and administrators can do it.',
      justification: 'Justification',
      justificationHint: 'Why it is not a problem on this website. At least 20 characters.',
      evidence: 'Evidence',
      evidenceHint: 'What you checked: a test, a screenshot, a link or a result. At least 10 characters.',
      discardNote:
        'The original detection and its history are kept. The element leaves the counts and the score, and is listed apart with your justification, your name and the date. You can lift the discard whenever you want.',
      decidedBy: 'Decided by {{name}}, {{date}}.',
      justificationLine: 'Justification: {{text}}',
      evidenceLine: 'Evidence: {{text}}',
    },
    check: {
      title: 'Result of the latest check',
      description: 'What a later analysis or a manual review found.',
      none: 'Not verified',
      noneHint: 'No analysis or review has verified this element since it was detected.',
      results: {
        'sigue-detectandose': 'Still detected',
        'no-detectado': 'Not detected in the latest check',
        'no-comprobable': 'Not confirmed automatically',
        'requiere-revision': 'Needs manual review',
      },
      sources: { automatica: 'Automatic analysis', manual: 'Manual review' },
      on: '{{source}} of {{date}}',
      devices: 'Devices: {{list}}.',
      stillPending:
        'The fix is still awaiting verification: a result other than “Not detected” does not count it as checked.',
      reasons: {
        detectado: 'The analysis found this element again.',
        'detectado-en-revision':
          'The analysis found it, but cannot decide whether it is a problem: it needs a manual review.',
        comprobado:
          'A comparable analysis looked at the page and no longer detected it. This does not certify that the website complies with any regulation.',
        'revision-favorable': 'A person reviewed it by hand and it passes.',
        'revision-desfavorable': 'A person reviewed it by hand and it still has the problem.',
        'pagina-fuera': 'The page was not in that analysis, so it could not be confirmed.',
        'pagina-no-analizada':
          'The website did not let us analyse the page (blocked, limited or with an error): we cannot know whether it was fixed.',
        dispositivo:
          'The analysis did not cover every device where it was detected (desktop and mobile): it is not comparable.',
        versiones:
          'That analysis used another version of the engine or the rules, so it is not comparable with the one that detected the problem. Check it again.',
        'regla-no-aplicada':
          'The test found no element of this kind on the page: you may have removed it or moved it. Check it by hand.',
        muestra:
          'The test only lists some of the elements on the page and this one is not among them: we cannot be sure it is fixed.',
        'posible-cambio':
          'This element no longer fails with its original selector, but the page still has other problems of the same kind. It could be the same one under another address in the code: the scan will not confirm it on its own. Review it by hand.',
        'ventana-emergente':
          'It is in a pop-up or a notice that may not appear when analysing. Review it by hand.',
        'sin-registro': 'This test does not record the elements that pass: it needs a manual review.',
        incierta: 'This is something automated tests cannot decide: it needs a manual review.',
      },
    },
    verify: {
      action: 'Check the fix',
      hint: 'Opens the summary before the analysis: there you see which pages will be checked and how many pages it will use. It does not start until you press the scan button.',
      many_one: 'Check the applied fix',
      many_other: 'Check the {{count}} applied fixes',
    },
    review: {
      title: 'Record a manual review',
      hint: 'Use it when the analysis cannot decide (a pop-up, an element that has moved, a point to review) or you prefer to check it yourself. Follow the steps in “How to check it”.',
      outcome: 'Result of the review',
      favorable: 'It passes',
      favorableHint: 'It is no longer a problem on the devices you reviewed.',
      desfavorable: 'It still has the problem',
      desfavorableHint: 'The problem is still there.',
      devices: 'Devices you reviewed',
      devicesHint: 'A favourable review has to cover every device where it was detected.',
      note: 'What you did and what you saw',
      noteHint: 'At least 10 characters. It stays in the history.',
      save: 'Record review',
      saved: 'Review recorded.',
      errors: {
        ambito: 'A favourable review has to cover every device where it was detected.',
        archivada: 'This work is archived: reopen it before recording another review.',
        permiso: 'Your role does not let you record reviews.',
        generic: 'The review could not be recorded. Please try again.',
      },
    },
    archive: {
      title: 'Archive fix work',
      evidence: 'Evidence that justifies it',
      evidenceAuto:
        'A comparable analysis of {{date}} looked at the page and no longer detected this element.',
      evidenceManual: 'A favourable manual review of {{date}}.',
      noteLabel: 'Archive note',
      noteHint: 'Optional.',
      action: 'Archive fix work',
      archiving: 'Archiving…',
      done: 'Work archived.',
      notCertification:
        'Archiving does not certify that the website complies with any regulation: it closes the work on this element with the evidence of a check. If a comparable check detects it again, the work reopens as pending.',
      archivedBy: 'Work archived on {{date}} by {{name}}.',
      why: {
        'sin-comprobacion':
          'It can be archived when a comparable analysis no longer detects the problem, or when a favourable manual review is recorded.',
        'no-favorable':
          'It cannot be archived yet: the latest check is not favourable. It can be archived after a check that does not detect it, or a favourable manual review.',
        'anterior-a-la-correccion':
          'The latest favourable check is older than the fix you marked as applied. Check it again.',
        'ya-archivada': 'The work is already archived.',
      },
      errors: {
        'sin-evidencia': 'There is no longer a check that justifies archiving it. Review the result.',
        permiso: 'Your role does not let you archive.',
        generic: 'It could not be archived. Please try again.',
      },
    },
    history: {
      title: 'History',
      loading: 'Loading the history…',
      empty: 'There is nothing in the history yet.',
      error: 'The history could not be loaded.',
      note: 'Note: {{text}}',
      created: 'This element started to be followed.',
      createdMigrated: 'An earlier decision about the group this element belonged to is kept ({{from}}).',
      work: 'Work tracking: from “{{from}}” to “{{to}}”.',
      validation: 'Detection validation: from “{{from}}” to “{{to}}”.',
      lifted: 'A discard that said “{{justification}}” (evidence: “{{evidence}}”) was lifted.',
      check: 'Automatic check: {{result}}. {{reason}}',
      review: 'Manual review ({{outcome}}) on {{devices}}: {{text}}',
      guided: 'Guided review of criterion {{criterion}}: {{outcome}}. Devices: {{devices}}. {{text}}',
      archive: 'Fix work archived.',
      reopenManual: 'Archived work reopened by choosing a status.',
      reopenAuto: 'Work reopened as pending: a check detected it again.',
      none: 'no decision',
      actor: '{{name}}, {{date}}',
    },
    summary: {
      figures: {
        tracked: 'Elements followed',
        applied: 'Fix applied',
        verified: 'Verified by a check',
      },
      title: 'Work tracking and decisions',
      intro:
        'Three separate counts: how many elements your team follows, how many have a declared fix, and how many a check no longer detects. “Fix applied” does not verify them: you still need to check them.',
      none: 'Your team has not recorded anything about the elements of this website yet.',
      tracked_one: '{{count}} element followed',
      tracked_other: '{{count}} elements followed',
      discarded_one:
        '{{count}} detection discarded with justification: it counts neither in the totals nor in the score.',
      discarded_other:
        '{{count}} detections discarded with justification: they count neither in the totals nor in the score.',
      open: 'Open the follow-up',
    },
    tab: {
      name: 'Follow-up',
      title: 'Follow-up of the fixes',
      intro:
        'The work status (for example “Fix applied”), the check result and the discards. Declaring the fix does not verify it: you need to check it.',
      viewProblems: 'View problems',
      loadFailed: 'The follow-up could not be loaded.',
      empty:
        'There are no recorded corrections yet. Start from the problems found and record the status of an element.',
      filters: 'Filters',
      work: 'Work tracking',
      check: 'Check result',
      all: 'All',
      shown_one: 'Showing {{shown}} of {{total}} element.',
      shown_other: 'Showing {{shown}} of {{total}} elements.',
      none: 'No element matches the filters.',
      element: 'Element',
      problem: 'Problem',
      page: 'Page',
      states: 'Status',
      open: 'See the element',
      openPage: 'See the page',
      noCapture: 'It is no longer in the latest analysis.',
      checkAll: 'Check the applied fixes',
      checkAllHint_one: 'The fix marked as applied will be checked from the summary before the analysis.',
      checkAllHint_other:
        'The {{count}} fixes marked as applied will be checked from the summary before the analysis.',
      checkTooMany:
        'There are applied fixes on more than {{max}} pages: they will be checked in parts. Open each one from its own page.',
      discards: 'Detections discarded with justification',
      discardsIntro: 'They leave the counts and the score. The original detection and its history are kept.',
      discardedBy: 'Discarded by {{name}}, {{date}}.',
      lift: 'To lift a discard, open the element.',
      manage: 'Manage the status',
      showArchived: 'Show archived work',
    },
    problem: {
      title: 'Problem status',
      counts_one: '{{count}} element followed out of {{total}}',
      counts_other: '{{count}} elements followed out of {{total}}',
      bulkTitle: 'Apply the same status to every element',
      bulkHint:
        'It is saved separately on each element, with its own history. Use it when the status is the same for all.',
      bulkApplies_one: 'It will apply to {{count}} element of this problem.',
      bulkApplies_other: 'It will apply to all {{count}} elements of this problem.',
      discardedElements_one: '{{count}} element discarded with justification',
      discardedElements_other: '{{count}} elements discarded with justification',
      allDiscarded:
        'Every element of this problem is discarded with justification: it counts neither in the totals nor in the score.',
    },
    discardedList: {
      title: 'Problems discarded with justification',
      intro:
        'All their elements are discarded, so they count neither in the totals nor in the score. Open one to see the justification or lift the discard.',
    },
  },
  site: {
    created: 'Project created. The analysis has not started yet.',
    back: 'Your websites',
    address: 'Address',
    profile: 'Regulatory profile',
    tabs: { pages: 'Pages', follow: 'Follow-up' },
    details: 'Website details',
    analysisStandard: 'Standard used in the scan',
    harmonised: 'Current harmonised European reference',
    standardsNote:
      'The harmonised reference is the version the Official Journal of the European Union cites today (Decision (EU) 2021/1339). The standard used in the scan depends on the website’s regulatory profile.',
    verificationLink: 'go to the ownership verification',
    verifyAction: 'Verify ownership',
    notFound: 'This website does not exist or does not belong to your organisation.',
    verification: {
      steps: {
        choose: 'Choose how',
        publish: 'Publish the code',
        check: 'Check it',
        done: 'done',
      },
      title: 'Ownership verification',
      intro: 'Verifying the website shows that it is yours.',
      without: 'Not verified: single-page scan and quick scan (3 pages).',
      with: 'With ownership verified: full scan (up to 50 pages), which does not depend on robots.txt.',
      verified: 'Ownership verified on {{date}} ({{method}}).',
      readOnly: 'Ask someone with the editor role or above to verify it.',
      method: 'How you want to verify it',
      methods: { meta: 'Meta tag', dns: 'DNS record', fichero: 'File' },
      methodHints: {
        meta: 'The quickest, if you can edit the code of the home page.',
        dns: 'For whoever manages the domain; it works even if a firewall blocks the website. It can take a few hours.',
        fichero: 'If you can upload files to the website’s server.',
      },
      metaSteps: 'Paste this tag inside <head> on {{url}}, publish the change and press “Check now”:',
      dnsSteps:
        'In your domain’s DNS, create a TXT record named {{host}} with this value, and press “Check now”:',
      dnsNote: 'DNS changes can take a few hours to show.',
      fileSteps: 'Create a text file reachable at {{url}} with this content and press “Check now”:',
      tag: 'Tag',
      record: 'TXT record value',
      fileContent: 'File content',
      copy: 'Copy',
      copied: 'Copied',
      check: 'Check now',
      checking: 'Checking the verification…',
      checkingHint:
        'We are checking the code on your website. You can leave this screen; the result will appear here.',
      checkingWait:
        'The check is still running. If the website is slow to respond, it may take a few more seconds.',
      checkingSlow:
        'It has been more than 15 seconds. If the website does not respond, the check will fail and you can try another method.',
      done: 'Done: the ownership of the website is verified.',
      errors: {
        'meta-missing':
          'We cannot find the tag on {{url}}. Make sure it is inside <head> and that the published page already has it.',
        'file-missing': 'The file exists but does not contain the code. Check its content.',
        'dns-missing': 'We cannot see the TXT record yet. DNS changes can take a few hours: try again later.',
        http: 'We could not complete the verification because the website returned an HTTP {{status}} error. Check that the address is correct and that the page is available. You can also try another verification method, such as the DNS record.',
        unreachable:
          'We could not complete the verification because we could not connect to the website. Check that the address is correct and that the website is available. You can also try another verification method, such as the DNS record.',
        timeout: 'The check did not finish. Try again in a moment.',
        limite: 'You have run many checks in a short time. Wait a while and try again.',
      },
    },
    settings: {
      title: 'Settings',
      scope:
        'These settings only affect this website. Your personal details and your password are in <profile>My profile</profile> and <security>Security</security>.',
      profileUnset:
        'You have not chosen a regulatory profile yet. Choose it here or when you prepare a scan. Until then, the analysis uses WCAG 2.2 level AA as its technical reference.',
      name: 'Name',
      save: 'Save changes',
      saved: 'Changes saved.',
      profileNote:
        'The profile only says which regulation is the reference when scanning. Choosing it does not certify or prove that your website meets the law, nor does it decide whether the law applies to you: that depends on your activity.', // claims-ok
      cardImage: {
        title: 'Preview image',
        intro:
          'Update the home-page screenshot and the icon that appear on this website’s card in “Your websites”.\n\nThis action does not start an analysis and does not use pages from your plan. The image can also update when you run a new analysis of the home page.',
        action: 'Update preview',
        working: 'Updating…',
        done: 'The new picture is being captured. It will appear in “Your websites” in a few seconds.',
        errors: {
          permiso: 'Your role cannot update this website’s image.',
          limite: 'You have asked to update the image too many times. Wait a while and try again.',
          generic: 'We could not update the image. Try again.',
        },
      },
      errors: {
        permiso: 'Your role cannot change this website’s settings. Nothing was saved.',
        gone: 'This website no longer exists. Nothing was saved.',
        generic: 'We could not save the changes. Try again.',
      },
    },
    remove: {
      title: 'Delete the website',
      intro: 'The website and all its data will be deleted. This cannot be undone.',
      dataTitle: 'This will delete:',
      data: {
        analyses: 'The scans and their results.',
        follow: 'The follow-up of corrections, with its history.',
        reviews: 'The recorded manual reviews.',
        ownership: 'The ownership verification and the WordPress link, if any.',
      },
      typeName: 'Type “{{name}}” to confirm',
      button: 'Delete the website',
      confirmTitle: 'Delete {{name}}?',
      confirm: 'Delete',
      cancel: 'Cancel',
    },
  },
  invitation: {
    title: 'Invitation to an organisation',
    details: '{{inviter}} invites you to {{organization}} with the {{role}} role.',
    accept: 'Accept the invitation',
    acceptedTitle: 'Invitation accepted',
    accepted: 'You are now a member of the organisation.',
    continue: 'Go to the dashboard',
    loginFirst: 'To accept it, sign in or create an account with the email the invitation was sent to.',
    login: 'Sign in',
    signup: 'Create an account',
    invalid:
      'The invitation is not valid, has expired or is for another email. Sign in with the account it was sent to or ask for another one.',
  },
};
